import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { AlertCircle, Camera, CheckCircle, Download, Filter, Fuel, Image, Play, Plus, Save, Send, Shield, Trash2, Truck, Upload, Wrench, X } from "lucide-react";
import { Badge, Button, Card, FileUploadInput, Input, Modal, Select, Spinner, Textarea } from "../shared/ui";
import { useAuth } from "../App";
import { MachineConflictModal, parseMachineConflict } from "../components/MachineConflictModal";
import ReadingMismatchModal from "../components/ReadingMismatchModal";
import MachineRegistryTab from "../components/MachineRegistryTab";
import MachineReceiptsTab from "../components/MachineReceiptsTab";
import MachineTravelHistoryTab from "../components/machines/MachineTravelHistoryTab";
import MachineUsageEditor from "../components/machines/MachineUsageEditor";
import MachineRequiredTicketsEditor from "../components/tickets/MachineRequiredTicketsEditor";
import { localTodayISO } from "../lib/localDate";
import MachineTimelineTab from "../components/machines/MachineTimelineTab";
import DryHireStartModal from "../components/machines/DryHireStartModal";
import { uploadReceipt } from "../lib/uploadReceipt";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ===== Fleet Schedule View — Gantt-style 2-week calendar across all machines =====
const FleetScheduleView = ({ machines, canEdit, onPickMachine }) => {
  const [hires, setHires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draggedId, setDraggedId] = useState(null);
  const [dragOverCell, setDragOverCell] = useState(null); // { machineId, date }
  const [windowStart, setWindowStart] = useState(() => localTodayISO());
  const [typeFilter, setTypeFilter] = useState('all');

  const fetchHires = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/general-hires`);
      setHires(res.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchHires(); }, []);

  const reschedule = async (hireId, newDate, newMachineId) => {
    const h = hires.find(x => x.id === hireId);
    if (!h) return;
    if (h.status !== 'scheduled') {
      const { toast } = await import('sonner');
      toast.error("Only scheduled hires can be moved");
      return;
    }
    if (h.work_date === newDate && h.machine_id === newMachineId) return;
    const targetMachine = machines.find(m => m.id === newMachineId);
    // Optimistic update
    setHires(prev => prev.map(x => x.id === hireId ? {
      ...x,
      work_date: newDate,
      machine_id: newMachineId,
      machine_name: targetMachine?.name || x.machine_name,
      machine_type: targetMachine?.type || x.machine_type,
      machine_rego: targetMachine?.rego || x.machine_rego
    } : x));
    try {
      const payload = { work_date: newDate };
      if (newMachineId !== h.machine_id) {
        payload.machine_id = newMachineId;
      }
      await axios.put(`${API}/general-hires/${hireId}`, payload);
      const { toast } = await import('sonner');
      const machineMsg = newMachineId !== h.machine_id ? ` to ${targetMachine?.name}` : '';
      toast.success(`Moved to ${new Date(newDate).toLocaleDateString('en-AU')}${machineMsg}`);
    } catch (e) {
      const { toast } = await import('sonner');
      toast.error(e.response?.data?.detail || "Failed to move — reverting");
      fetchHires();
    }
  };

  // Build 14-day window
  const days = (() => {
    const out = [];
    const base = new Date(windowStart + 'T00:00:00');
    for (let i = 0; i < 14; i++) {
      const d = new Date(base.getTime() + i * 86400000);
      out.push(d.toISOString().slice(0, 10));
    }
    return out;
  })();

  const today = localTodayISO();
  const machineTypes = Array.from(new Set(machines.map(m => m.type).filter(Boolean))).sort();
  const filteredMachines = machines.filter(m => typeFilter === 'all' || m.type === typeFilter);
  // Lookup: hires[machine_id][date] = [hire,...]
  const hireMap = {};
  for (const h of hires) {
    if (!h.machine_id || !h.work_date) continue;
    if ((days[0] && h.work_date < days[0]) || h.work_date > days[days.length - 1]) continue;
    if (!['scheduled', 'active'].includes(h.status)) continue;
    hireMap[h.machine_id] = hireMap[h.machine_id] || {};
    hireMap[h.machine_id][h.work_date] = hireMap[h.machine_id][h.work_date] || [];
    hireMap[h.machine_id][h.work_date].push(h);
  }

  const navWindow = (deltaDays) => {
    const d = new Date(windowStart + 'T00:00:00');
    d.setDate(d.getDate() + deltaDays);
    setWindowStart(d.toISOString().slice(0, 10));
  };

  if (loading) {
    return <div className="py-12 text-center"><Spinner size="lg" /></div>;
  }

  return (
    <div className="space-y-3" data-testid="fleet-schedule-view">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-bold uppercase text-slate-500">Filter:</span>
          <button
            onClick={() => setTypeFilter('all')}
            className={`px-3 py-1 rounded-full text-xs font-bold border ${typeFilter === 'all' ? 'bg-orange-500 text-slate-900 border-orange-500' : 'bg-white text-slate-600 border-slate-200'}`}
            data-testid="fleet-filter-all"
          >
            All ({machines.length})
          </button>
          {machineTypes.map(t => (
            <button
              key={t}
              onClick={() => setTypeFilter(t)}
              className={`px-3 py-1 rounded-full text-xs font-bold border ${typeFilter === t ? 'bg-orange-500 text-slate-900 border-orange-500' : 'bg-white text-slate-600 border-slate-200'}`}
              data-testid={`fleet-filter-${t}`}
            >
              {t.replace('_', ' ')}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1" data-testid="fleet-window-nav">
          <Button size="sm" variant="outline" onClick={() => navWindow(-7)} data-testid="fleet-prev-week">← Prev week</Button>
          <Button size="sm" variant="outline" onClick={() => setWindowStart(today)} data-testid="fleet-today">Today</Button>
          <Button size="sm" variant="outline" onClick={() => navWindow(7)} data-testid="fleet-next-week">Next week →</Button>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800">
        <strong>Tip:</strong> drag any scheduled hire to a different machine row OR a different day. Moves machine + date in one go.
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
        <div className="min-w-[1200px]">
          {/* Header row: machine column header + 14 day columns */}
          <div className="grid sticky top-0 bg-slate-100 border-b border-slate-200 z-10" style={{ gridTemplateColumns: '180px repeat(14, minmax(80px, 1fr))' }}>
            <div className="p-2 text-xs font-bold uppercase text-slate-600 border-r border-slate-200">Machine</div>
            {days.map(d => {
              const dt = new Date(d + 'T00:00:00');
              const isToday = d === today;
              const dow = dt.toLocaleDateString('en-AU', { weekday: 'short' });
              return (
                <div key={d} className={`p-1.5 text-center border-r border-slate-200 ${isToday ? 'bg-amber-100' : ''}`}>
                  <p className="text-[10px] font-bold uppercase text-slate-500">{dow}</p>
                  <p className={`text-xs font-bold ${isToday ? 'text-orange-700' : 'text-slate-700'}`}>{dt.getDate()} {dt.toLocaleString('en-AU', { month: 'short' })}</p>
                </div>
              );
            })}
          </div>

          {/* Machine rows */}
          {filteredMachines.length === 0 ? (
            <div className="text-center py-8 text-slate-500 text-sm">No machines match this filter</div>
          ) : (
            filteredMachines.map(m => (
              <div key={m.id} className="grid border-b border-slate-100" style={{ gridTemplateColumns: '180px repeat(14, minmax(80px, 1fr))' }}>
                <button
                  type="button"
                  onClick={() => onPickMachine(m)}
                  className="p-2 text-left border-r border-slate-200 hover:bg-slate-50 transition-colors"
                  data-testid={`fleet-machine-row-${m.id}`}
                >
                  <p className="font-bold text-sm truncate">{m.name}</p>
                  <p className="text-[10px] text-slate-500 uppercase">{m.type?.replace('_', ' ')} {m.rego ? `· ${m.rego}` : ''}</p>
                  <Badge variant={m.status === 'available' ? 'success' : m.status === 'in_use' ? 'warning' : 'default'} className="text-[9px] mt-1">{m.status?.replace('_', ' ')}</Badge>
                </button>
                {days.map(d => {
                  const cellHires = (hireMap[m.id] && hireMap[m.id][d]) || [];
                  const isDragOver = dragOverCell && dragOverCell.machineId === m.id && dragOverCell.date === d;
                  return (
                    <div
                      key={d}
                      onDragOver={(e) => { if (canEdit) { e.preventDefault(); if (!dragOverCell || dragOverCell.machineId !== m.id || dragOverCell.date !== d) setDragOverCell({ machineId: m.id, date: d }); } }}
                      onDragLeave={() => setDragOverCell(null)}
                      onDrop={(e) => {
                        e.preventDefault();
                        const id = e.dataTransfer.getData('text/plain');
                        setDragOverCell(null);
                        setDraggedId(null);
                        if (id) reschedule(id, d, m.id);
                      }}
                      className={`p-1 border-r border-slate-100 min-h-[60px] ${isDragOver ? 'bg-orange-100 ring-2 ring-orange-400 ring-inset' : d === today ? 'bg-amber-50/50' : 'bg-white'}`}
                      data-testid={`fleet-cell-${m.id}-${d}`}
                    >
                      <div className="space-y-1">
                        {cellHires.map(h => (
                          <div
                            key={h.id}
                            draggable={h.status === 'scheduled' && canEdit}
                            onDragStart={(e) => { e.dataTransfer.setData('text/plain', h.id); setDraggedId(h.id); }}
                            onDragEnd={() => { setDraggedId(null); setDragOverCell(null); }}
                            onClick={() => onPickMachine(m)}
                            className={`p-1 rounded text-[10px] cursor-pointer border transition-all ${
                              draggedId === h.id ? 'opacity-40' : ''
                            } ${h.status === 'active' ? 'bg-amber-100 border-amber-300' : 'bg-blue-50 border-blue-200 hover:border-blue-400 hover:shadow-sm'}`}
                            title={h.status === 'scheduled' ? 'Drag to reschedule, click to manage' : 'Click to manage'}
                            data-testid={`fleet-hire-${h.id}`}
                          >
                            <p className="font-bold truncate">{h.client_name || 'TBC'}</p>
                            <p className="text-slate-600 truncate">{h.start_time || ''}{h.operator_name ? ` · ${h.operator_name.split(' ')[0]}` : ''}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

const MachinesPage = () => {
  const { user } = useAuth();
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedMachine, setSelectedMachine] = useState(null);
  const [filter, setFilter] = useState("all");
  const [topView, setTopView] = useState('list'); // list | fleet_schedule

  // Only admin and office_staff can create/edit machines (not managers, supervisors, or workers)
  const canCreate = ['admin', 'office_staff'].includes(user?.role);
  const canEdit = ['admin', 'office_staff'].includes(user?.role);

  useEffect(() => {
    fetchMachines();
  }, []);

  const fetchMachines = async () => {
    try {
      const res = await axios.get(`${API}/machines`);
      setMachines(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const filteredMachines = machines.filter(m => {
    if (filter === "all") return true;
    return m.status === filter;
  });

  const statusColors = {
    available: "success",
    in_use: "warning",
    maintenance: "info",
    out_of_service: "error"
  };

  const typeLabels = {
    tower_crane: "Tower Crane",
    mobile_crane: "Mobile Crane",
    crawler_crane: "Crawler Crane",
    overhead_crane: "Overhead Crane",
    rigging_equipment: "Rigging Equipment",
    other: "Other"
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="machines-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold uppercase tracking-tight font-heading">Machines & Equipment</h1>
          <p className="text-slate-600">{filteredMachines.length} machine(s) found</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="machines-top-view-toggle">
            <button
              onClick={() => setTopView('list')}
              className={`px-3 py-1.5 text-xs font-bold rounded-md ${topView === 'list' ? 'bg-orange-500 text-slate-900' : 'text-slate-600 hover:bg-slate-50'}`}
              data-testid="machines-view-list"
            >
              List
            </button>
            <button
              onClick={() => setTopView('fleet_schedule')}
              className={`px-3 py-1.5 text-xs font-bold rounded-md ${topView === 'fleet_schedule' ? 'bg-orange-500 text-slate-900' : 'text-slate-600 hover:bg-slate-50'}`}
              data-testid="machines-view-fleet"
            >
              Fleet Schedule
            </button>
          </div>
          {canCreate && topView === 'list' && (
            <Button onClick={() => { setSelectedMachine(null); setShowModal(true); }} data-testid="create-machine-btn">
              <Plus className="w-5 h-5 mr-2" />
              Add Machine
            </Button>
          )}
        </div>
      </div>

      {topView === 'fleet_schedule' && (
        <FleetScheduleView machines={machines} canEdit={canEdit} onPickMachine={(m) => { setSelectedMachine(m); setShowModal(true); }} />
      )}

      {topView === 'list' && (
        <>

      {/* Status Filter */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {["all", "available", "in_use", "maintenance", "out_of_service"].map(status => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-4 py-2 rounded-md font-semibold text-sm uppercase whitespace-nowrap transition-colors ${
              filter === status 
                ? 'bg-orange-500 text-slate-900' 
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {status.replace('_', ' ')}
          </button>
        ))}
      </div>

      {/* Machines Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredMachines.map(machine => (
          <Card 
            key={machine.id}
            interactive
            onClick={() => { setSelectedMachine(machine); setShowModal(true); }}
            data-testid={`machine-card-${machine.id}`}
          >
            <div className="flex items-start justify-between mb-3">
              <Badge variant={statusColors[machine.status]}>{machine.status.replace('_', ' ')}</Badge>
              <Truck className="w-6 h-6 text-orange-500" />
            </div>
            <h3 className="text-xl font-bold font-heading uppercase mb-2">{machine.name}</h3>
            <p className="text-sm text-slate-500 uppercase mb-2">{typeLabels[machine.machine_type] || machine.machine_type}</p>
            <div className="flex flex-wrap gap-2 text-sm text-slate-600">
              {machine.registration_number && (
                <span className="bg-slate-100 px-2 py-1 rounded">{machine.registration_number}</span>
              )}
              {machine.capacity && (
                <span className="bg-slate-100 px-2 py-1 rounded">{machine.capacity}</span>
              )}
            </div>
            {machine.manufacturer && (
              <p className="text-xs text-slate-400 mt-3">{machine.manufacturer} {machine.model}</p>
            )}
          </Card>
        ))}

        {filteredMachines.length === 0 && (
          <div className="col-span-full text-center py-12 text-slate-500">
            <Truck className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p className="text-lg font-semibold">No machines found</p>
            <p className="text-sm">Add your first machine to get started</p>
          </div>
        )}
      </div>
        </>
      )}

      {/* Machine Modal */}
      <MachineModal 
        isOpen={showModal}
        onClose={() => { setShowModal(false); setSelectedMachine(null); }}
        machine={selectedMachine}
        onSave={fetchMachines}
        canEdit={canEdit}
      />
    </div>
  );
};


// ===== Scheduled General Hire Jobs for a machine =====
const ScheduleTab = ({ machineId, canEdit, clients, employees }) => {
  const { user } = useAuth();
  const [hires, setHires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showOneOff, setShowOneOff] = useState(false);
  const [selectedHire, setSelectedHire] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [operators, setOperators] = useState([]);
  const [dogmen, setDogmen] = useState([]);
  const [internalJobs, setInternalJobs] = useState([]);
  const [hireKind, setHireKind] = useState('client'); // 'client' | 'internal'
  const [form, setForm] = useState({
    client_id: "",
    client_name: "",
    client_contact: "",
    client_phone: "",
    internal_job_id: "",
    location: "",
    description: "",
    hourly_rate: "",
    work_date: new Date(Date.now() + 86400000).toISOString().split('T')[0],
    start_time: "07:00",
    operator_id: "",
    dogman_ids: [],
    notes: ""
  });
  const [oneOff, setOneOff] = useState({ name: "", contact_person: "", phone: "", notes: "" });

  const fetchHires = async () => {
    setLoading(true);
    try {
      const [hiresRes, opsRes, dogsRes, jobsRes] = await Promise.all([
        axios.get(`${API}/general-hires`),
        axios.get(`${API}/users/capable/operator`).catch(() => ({ data: [] })),
        axios.get(`${API}/users/capable/dogman`).catch(() => ({ data: [] })),
        axios.get(`${API}/jobs?status=in_progress`).catch(() => ({ data: [] })),
      ]);
      const mine = (hiresRes.data || []).filter(h => h.machine_id === machineId && ["scheduled", "active"].includes(h.status));
      mine.sort((a, b) => (a.work_date || '').localeCompare(b.work_date || ''));
      setHires(mine);
      setOperators(opsRes.data || []);
      setDogmen(dogsRes.data || []);
      setInternalJobs(jobsRes.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchHires(); }, [machineId]);

  const createOneOffClient = async () => {
    if (!oneOff.name.trim()) {
      const { toast } = await import('sonner');
      toast.error("Client name required");
      return;
    }
    try {
      const res = await axios.post(`${API}/clients`, { ...oneOff, client_type: "one_off" });
      const { toast } = await import('sonner');
      toast.success("One-off client created");
      setForm(f => ({ ...f, client_id: res.data.id, client_name: res.data.name, client_contact: res.data.contact_person || "", client_phone: res.data.phone || "" }));
      setShowOneOff(false);
      setOneOff({ name: "", contact_person: "", phone: "", notes: "" });
    } catch (e) {
      const { toast } = await import('sonner');
      toast.error(e.response?.data?.detail || "Failed to create client");
    }
  };

  const submit = async () => {
    if (!form.operator_id) {
      const { toast } = await import('sonner');
      toast.error("Operator is required to schedule a job");
      return;
    }
    if (hireKind === 'internal' && !form.internal_job_id) {
      const { toast } = await import('sonner');
      toast.error("Pick an internal job to schedule onto");
      return;
    }
    if (!form.work_date) {
      const { toast } = await import('sonner');
      toast.error("Work date is required");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/general-hires`, {
        ...form,
        machine_id: machineId,
        hourly_rate: form.hourly_rate ? parseFloat(form.hourly_rate) : null,
        status: "scheduled",
        // Internal jobs: don't require a client; pass internal_job_id only
        ...(hireKind === 'internal'
          ? { internal_job_id: form.internal_job_id, client_id: null, client_name: "" }
          : { internal_job_id: null }),
      });
      const { toast } = await import('sonner');
      toast.success("Scheduled hire created");
      setShowForm(false);
      setHireKind('client');
      setForm({
        client_id: "", client_name: "", client_contact: "", client_phone: "",
        internal_job_id: "",
        location: "", description: "", hourly_rate: "",
        work_date: new Date(Date.now() + 86400000).toISOString().split('T')[0],
        start_time: "07:00", operator_id: "", dogman_ids: [], notes: ""
      });
      fetchHires();
    } catch (e) {
      const { toast } = await import('sonner');
      toast.error(e.response?.data?.detail || "Failed to schedule");
    } finally {
      setSubmitting(false);
    }
  };

  const today = new Date().toISOString().split('T')[0];
  const upcoming = hires.filter(h => (h.work_date || '') >= today);
  const past = hires.filter(h => (h.work_date || '') < today);

  // Drag & drop reschedule
  const [draggedId, setDraggedId] = useState(null);
  const [dragOverDate, setDragOverDate] = useState(null);
  const rescheduleHire = async (hireId, newDate) => {
    const h = hires.find(x => x.id === hireId);
    if (!h || h.work_date === newDate) return;
    if (h.status !== 'scheduled') {
      const { toast } = await import('sonner');
      toast.error("Only scheduled hires can be moved");
      return;
    }
    // Optimistic update
    setHires(prev => prev.map(x => x.id === hireId ? { ...x, work_date: newDate } : x));
    try {
      await axios.put(`${API}/general-hires/${hireId}`, { work_date: newDate });
      const { toast } = await import('sonner');
      toast.success(`Moved to ${new Date(newDate).toLocaleDateString('en-AU')}`);
    } catch (e) {
      const { toast } = await import('sonner');
      toast.error(e.response?.data?.detail || "Failed to move — reverting");
      fetchHires(); // revert
    }
  };

  // Build 14-day window from today
  const buildDays = () => {
    const days = [];
    const base = new Date(today + 'T00:00:00');
    for (let i = 0; i < 14; i++) {
      const d = new Date(base.getTime() + i * 86400000);
      days.push(d.toISOString().slice(0, 10));
    }
    return days;
  };
  const calDays = buildDays();
  const hiresByDate = upcoming.reduce((acc, h) => {
    const k = h.work_date;
    if (!acc[k]) acc[k] = [];
    acc[k].push(h);
    return acc;
  }, {});

  const [viewMode, setViewMode] = useState('list'); // list | calendar

  return (
    <div className="space-y-4" data-testid="schedule-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-bold text-lg">Scheduled General Hire Jobs</h3>
          <p className="text-xs text-slate-500">Upload upcoming one-off hires. Operators can clock straight onto them on the day.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="schedule-view-toggle">
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1 text-xs font-bold rounded-md ${viewMode === 'list' ? 'bg-orange-500 text-slate-900' : 'text-slate-600 hover:bg-slate-50'}`}
              data-testid="schedule-view-list"
            >
              List
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              className={`px-3 py-1 text-xs font-bold rounded-md ${viewMode === 'calendar' ? 'bg-orange-500 text-slate-900' : 'text-slate-600 hover:bg-slate-50'}`}
              data-testid="schedule-view-calendar"
            >
              Calendar
            </button>
          </div>
          {canEdit && (
            <Button onClick={() => setShowForm(true)} data-testid="add-scheduled-hire-btn">
              <Plus className="w-4 h-4 mr-1" /> Schedule Hire
            </Button>
          )}
        </div>
      </div>

      {/* Calendar view — 14-day grid with drag to reschedule */}
      {!loading && viewMode === 'calendar' && (
        <div data-testid="schedule-calendar-view" className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <div className="p-2 bg-slate-50 border-b text-xs text-slate-600 text-center">
            <strong>Tip:</strong> drag any scheduled hire card onto a different day to reschedule. Only <em>scheduled</em> hires can be moved.
          </div>
          <div className="grid grid-cols-7 gap-0" style={{ gridAutoRows: 'minmax(110px, auto)' }}>
            {/* Weekday headings */}
            {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(dow => (
              <div key={dow} className="p-2 text-center text-[10px] font-bold uppercase bg-slate-100 border-b border-slate-200 text-slate-500">{dow}</div>
            ))}
            {/* Leading blanks so first day sits in the right weekday column (Mon=0 … Sun=6) */}
            {(() => {
              const firstDay = new Date(calDays[0] + 'T00:00:00');
              const jsDow = firstDay.getDay(); // 0=Sun..6=Sat
              const offset = (jsDow + 6) % 7; // Mon=0..Sun=6
              return Array.from({ length: offset }, (_, i) => (
                <div key={`blank-${i}`} className="min-h-[110px] border-r border-b border-slate-100 bg-slate-50/30" />
              ));
            })()}
            {calDays.map(date => {
              const dateObj = new Date(date + 'T00:00:00');
              const dayLabel = dateObj.getDate();
              const monthLabel = dateObj.toLocaleString('en-AU', { month: 'short' });
              const isToday = date === today;
              const dayHires = hiresByDate[date] || [];
              const isDragOver = dragOverDate === date;
              return (
                <div
                  key={date}
                  onDragOver={(e) => { e.preventDefault(); if (dragOverDate !== date) setDragOverDate(date); }}
                  onDragLeave={() => { if (dragOverDate === date) setDragOverDate(null); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const id = e.dataTransfer.getData('text/plain');
                    setDragOverDate(null);
                    setDraggedId(null);
                    if (id) rescheduleHire(id, date);
                  }}
                  className={`min-h-[110px] border-r border-b border-slate-100 p-1.5 ${isDragOver ? 'bg-orange-100 ring-2 ring-orange-400 ring-inset' : isToday ? 'bg-amber-50/50' : 'bg-white'}`}
                  data-testid={`cal-day-${date}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-xs font-bold ${isToday ? 'text-orange-600' : 'text-slate-600'}`}>
                      {dayLabel} <span className="font-normal text-[10px] text-slate-400">{monthLabel}</span>
                    </span>
                    {isToday && <span className="text-[9px] font-bold uppercase text-orange-600">Today</span>}
                  </div>
                  <div className="space-y-1">
                    {dayHires.map(h => (
                      <div
                        key={h.id}
                        draggable={h.status === 'scheduled' && canEdit}
                        onDragStart={(e) => { e.dataTransfer.setData('text/plain', h.id); setDraggedId(h.id); }}
                        onDragEnd={() => { setDraggedId(null); setDragOverDate(null); }}
                        onClick={() => setSelectedHire(h)}
                        className={`p-1.5 rounded text-[11px] cursor-pointer border transition-all ${
                          draggedId === h.id ? 'opacity-40' : ''
                        } ${h.status === 'active' ? 'bg-amber-100 border-amber-300' : h.status === 'completed' ? 'bg-green-100 border-green-300' : 'bg-blue-50 border-blue-200 hover:border-blue-400 hover:shadow-sm'}`}
                        title={h.status === 'scheduled' ? 'Drag to reschedule, click for details' : 'Click for details'}
                        data-testid={`cal-hire-${h.id}`}
                      >
                        <p className="font-bold truncate">{h.client_name || 'TBC'}</p>
                        <p className="text-slate-600 truncate">{h.start_time || ''} {h.operator_name ? `· ${h.operator_name.split(' ')[0]}` : ''}</p>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {loading ? <Spinner /> : viewMode === 'list' ? (
        <>
          {upcoming.length === 0 ? (
            <Card className="text-center py-10 text-slate-500">
              <p className="font-semibold">No upcoming scheduled hires</p>
              <p className="text-xs">Schedule one-off jobs so operators can clock on directly.</p>
            </Card>
          ) : (
            <div className="space-y-3">
              {upcoming.map(h => (
                <Card
                  key={h.id}
                  interactive
                  onClick={() => setSelectedHire(h)}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 cursor-pointer hover:border-orange-400 hover:shadow-md transition-all"
                  data-testid={`scheduled-hire-${h.id}`}
                >
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-bold">{h.is_internal ? (h.internal_job_title || 'Internal job') : (h.client_name || 'Unassigned client')}</h4>
                      <Badge variant={h.status === 'active' ? 'warning' : 'info'}>{h.status}</Badge>
                      {h.is_internal && <Badge variant="default" data-testid={`internal-badge-${h.id}`}>Internal</Badge>}
                      {h.work_date === today && <Badge variant="success">Today</Badge>}
                    </div>
                    <p className="text-sm text-slate-600">{new Date(h.work_date).toLocaleDateString('en-AU')} {h.start_time ? `@ ${h.start_time}` : ''}</p>
                    {h.location && <p className="text-xs text-slate-500">📍 {h.location}</p>}
                    {h.description && <p className="text-xs text-slate-600 italic">{h.description}</p>}
                    <p className="text-xs text-slate-500 mt-1">
                      Op: <strong>{h.operator_name || '—'}</strong>
                      {h.dogman_names?.length > 0 && <> · Dogman: {h.dogman_names.map(d => d.name || d).join(', ')}</>}
                      {h.hourly_rate && <> · ${h.hourly_rate}/hr</>}
                    </p>
                  </div>
                  <span className="text-xs uppercase font-bold text-orange-600 self-start sm:self-center">View / Manage →</span>
                </Card>
              ))}
            </div>
          )}
          {past.length > 0 && (
            <details className="mt-4">
              <summary className="text-xs font-bold text-slate-500 cursor-pointer">Past ({past.length})</summary>
              <div className="space-y-2 mt-2">
                {past.map(h => (
                  <div key={h.id} className="text-xs text-slate-500 bg-slate-50 p-2 rounded">
                    {new Date(h.work_date).toLocaleDateString('en-AU')} — {h.client_name} — {h.operator_name}
                  </div>
                ))}
              </div>
            </details>
          )}
        </>
      ) : null}

      {/* Create scheduled hire modal */}
      {showForm && (
        <Modal isOpen onClose={() => setShowForm(false)} title="Schedule General Hire Job" size="lg">
          <div className="space-y-3" data-testid="schedule-hire-form">
            {/* Hire kind toggle: External client vs Internal own job */}
            <div className="flex items-center gap-2 p-1 bg-slate-100 rounded-lg" data-testid="hire-kind-toggle">
              <button
                type="button"
                onClick={() => setHireKind('client')}
                className={`flex-1 px-3 py-1.5 text-xs font-bold rounded-md transition-colors ${hireKind === 'client' ? 'bg-orange-500 text-slate-900' : 'text-slate-600 hover:bg-slate-200'}`}
                data-testid="hire-kind-client"
              >
                For a Client
              </button>
              <button
                type="button"
                onClick={() => setHireKind('internal')}
                className={`flex-1 px-3 py-1.5 text-xs font-bold rounded-md transition-colors ${hireKind === 'internal' ? 'bg-orange-500 text-slate-900' : 'text-slate-600 hover:bg-slate-200'}`}
                data-testid="hire-kind-internal"
              >
                On Our Own Job
              </button>
            </div>

            {hireKind === 'internal' ? (
              <Select
                label="Internal Job *"
                value={form.internal_job_id}
                onChange={(e) => {
                  const job = internalJobs.find(j => j.id === e.target.value);
                  setForm(f => ({
                    ...f,
                    internal_job_id: e.target.value,
                    location: job?.location || f.location,
                    description: job?.title ? `Working on: ${job.title}` : f.description
                  }));
                }}
                options={[{ value: "", label: internalJobs.length ? "Select an active job" : "No active jobs found" }, ...internalJobs.map(j => ({ value: j.id, label: `${j.title}${j.client_name ? ` — ${j.client_name}` : ''}` }))]}
                testId="schedule-internal-job-select"
              />
            ) : (
              <div>
                <label className="text-xs font-bold uppercase text-slate-600">Client</label>
                <div className="flex gap-2 items-start">
                  <div className="flex-1 min-w-0">
                    <Select
                      value={form.client_id}
                      onChange={(e) => {
                        const c = clients.find(c => c.id === e.target.value);
                        setForm(f => ({ ...f, client_id: e.target.value, client_name: c?.name || "", client_contact: c?.contact_person || "", client_phone: c?.phone || "" }));
                      }}
                      options={[{ value: "", label: "Select client" }, ...clients.map(c => ({ value: c.id, label: `${c.name}${c.client_type === 'one_off' ? ' (one-off)' : ''}` }))]}
                      testId="schedule-client-select"
                    />
                  </div>
                  <Button type="button" variant="outline" className="shrink-0 whitespace-nowrap" onClick={() => setShowOneOff(true)} data-testid="open-one-off-btn">+ One-off</Button>
                </div>
              </div>
            )}

            {hireKind !== 'internal' && (
              <Input label="Client Contact Phone" value={form.client_phone} onChange={(e) => setForm(f => ({ ...f, client_phone: e.target.value }))} testId="schedule-client-phone" />
            )}

            <Input label="Site Location / Address" value={form.location} onChange={(e) => setForm(f => ({ ...f, location: e.target.value }))} testId="schedule-location" />
            <Textarea label="Description / Scope of Work" value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} rows={2} testId="schedule-description" />

            <div className="grid md:grid-cols-3 gap-3">
              <Input label="Work Date" type="date" value={form.work_date} onChange={(e) => setForm(f => ({ ...f, work_date: e.target.value }))} testId="schedule-work-date" />
              <Input label="Start Time" type="time" value={form.start_time} onChange={(e) => setForm(f => ({ ...f, start_time: e.target.value }))} testId="schedule-start-time" />
              <Input label="Hourly Rate ($)" type="number" value={form.hourly_rate} onChange={(e) => setForm(f => ({ ...f, hourly_rate: e.target.value }))} testId="schedule-rate" />
            </div>

            <div className="grid md:grid-cols-2 gap-3">
              <Select
                label="Operator (required)"
                value={form.operator_id}
                onChange={(e) => setForm(f => ({ ...f, operator_id: e.target.value }))}
                options={[{ value: "", label: operators.length ? "Select operator" : "No operators set up — tick Operator in someone's Profile" }, ...operators.map(u => ({ value: u.id, label: `${u.first_name} ${u.last_name} (${u.role})` }))]}
                testId="schedule-operator"
              />
              <div>
                <label className="text-xs font-bold uppercase text-slate-600">Dogman(s) / Rigger(s) — optional</label>
                <div className="max-h-24 overflow-y-auto border border-slate-200 rounded p-2 space-y-1">
                  {dogmen.length === 0 && <p className="text-xs text-slate-400 italic">No dogmen/riggers set up yet.</p>}
                  {dogmen.map(u => (
                    <label key={u.id} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={form.dogman_ids.includes(u.id)}
                        onChange={() => {
                          setForm(f => ({ ...f, dogman_ids: f.dogman_ids.includes(u.id) ? f.dogman_ids.filter(x => x !== u.id) : [...f.dogman_ids, u.id] }));
                        }}
                      />
                      {u.first_name} {u.last_name} <span className="text-xs text-slate-400">({u.role})</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <Textarea label="Notes (optional)" value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} testId="schedule-notes" />

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button onClick={submit} disabled={submitting} data-testid="submit-scheduled-hire-btn">
                {submitting ? 'Scheduling...' : 'Schedule Job'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* One-off client inline modal */}
      {showOneOff && (
        <Modal isOpen onClose={() => setShowOneOff(false)} title="Quick Add One-Off Client" size="default">
          <div className="space-y-3" data-testid="one-off-client-form">
            <p className="text-xs text-slate-500">Save a client on the fly. Marked as "one-off" — promote to a full client later once an account form is signed.</p>
            <Input label="Client / Company Name" value={oneOff.name} onChange={(e) => setOneOff(o => ({ ...o, name: e.target.value }))} testId="one-off-name" />
            <Input label="Contact Person" value={oneOff.contact_person} onChange={(e) => setOneOff(o => ({ ...o, contact_person: e.target.value }))} testId="one-off-contact" />
            <Input label="Phone" value={oneOff.phone} onChange={(e) => setOneOff(o => ({ ...o, phone: e.target.value }))} testId="one-off-phone" />
            <Textarea label="Notes (optional)" value={oneOff.notes} onChange={(e) => setOneOff(o => ({ ...o, notes: e.target.value }))} rows={2} testId="one-off-notes" />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowOneOff(false)}>Cancel</Button>
              <Button onClick={createOneOffClient} data-testid="save-one-off-btn">Save & Use</Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Hire detail / actions modal */}
      {selectedHire && (
        <HireDetailModal
          hire={selectedHire}
          user={user}
          canEdit={canEdit}
          clients={clients}
          operators={operators}
          dogmen={dogmen}
          onClose={() => setSelectedHire(null)}
          onChanged={() => { setSelectedHire(null); fetchHires(); }}
        />
      )}
    </div>
  );
};

// ===== Hire detail / actions modal =====
const HireDetailModal = ({ hire, user, canEdit, clients, operators, dogmen, onClose, onChanged }) => {
  const [busy, setBusy] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    client_id: hire.client_id || "",
    client_name: hire.client_name || "",
    client_phone: hire.client_phone || "",
    location: hire.location || "",
    description: hire.description || "",
    hourly_rate: hire.hourly_rate || "",
    work_date: hire.work_date || "",
    start_time: hire.start_time || "",
    operator_id: hire.operator_id || "",
    dogman_ids: hire.dogman_ids || [],
    notes: hire.notes || ""
  });

  const isPrivileged = ['admin', 'manager', 'office_staff'].includes(user?.role);
  const isCreator = hire.created_by === user?.id;
  const canDelete = isPrivileged || isCreator;
  const canStart = (isPrivileged || hire.operator_id === user?.id) && hire.status === 'scheduled';
  const canFinish = (isPrivileged || hire.operator_id === user?.id) && hire.status === 'active';
  const canComplete = isPrivileged && hire.status === 'active' && hire.clock_off_time;

  const action = async (path, opts = {}) => {
    setBusy(path);
    try {
      if (opts.method === 'DELETE') {
        await axios.delete(`${API}/general-hires/${hire.id}`);
      } else if (opts.method === 'PUT') {
        await axios.put(`${API}/general-hires/${hire.id}`, opts.data || {});
      } else {
        await axios.post(`${API}/general-hires/${hire.id}${path}`, opts.data || {});
      }
      const { toast } = await import('sonner');
      toast.success(opts.successMsg || 'Done');
      onChanged();
    } catch (e) {
      const { toast } = await import('sonner');
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setBusy(null);
    }
  };

  const onDelete = async () => {
    if (!window.confirm(`Delete this scheduled hire? This cannot be undone.`)) return;
    await action('', { method: 'DELETE', successMsg: 'Hire deleted' });
  };

  const onSaveEdit = async () => {
    const payload = { ...form };
    if (payload.hourly_rate) payload.hourly_rate = parseFloat(payload.hourly_rate);
    // Map dogman_ids -> dogman_names for back-compat
    if (Array.isArray(payload.dogman_ids)) {
      payload.dogman_names = payload.dogman_ids
        .map(id => {
          const u = dogmen.find(d => d.id === id);
          return u ? { id: u.id, name: `${u.first_name} ${u.last_name}` } : null;
        })
        .filter(Boolean);
    }
    if (payload.operator_id) {
      const op = operators.find(o => o.id === payload.operator_id);
      if (op) payload.operator_name = `${op.first_name} ${op.last_name}`;
    }
    if (payload.client_id) {
      const c = clients.find(c => c.id === payload.client_id);
      if (c) payload.client_name = c.name;
    }
    await action('', { method: 'PUT', data: payload, successMsg: 'Updated' });
  };

  return (
    <Modal isOpen onClose={onClose} title="General Hire Details" size="lg">
      <div className="space-y-4" data-testid="hire-detail-modal">
        {/* Header with status */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold text-lg">{hire.client_name || 'Unassigned client'}</h3>
            <p className="text-sm text-slate-500">{hire.machine_name}{hire.machine_rego ? ` · ${hire.machine_rego}` : ''}</p>
          </div>
          <Badge variant={hire.status === 'active' ? 'warning' : hire.status === 'completed' ? 'success' : 'info'}>{hire.status}</Badge>
        </div>

        {!editing ? (
          <>
            {/* Read-only details */}
            <div className="grid grid-cols-2 gap-3 text-sm bg-slate-50 rounded-lg p-3">
              <div><span className="text-xs uppercase font-bold text-slate-500 block">Date</span>{new Date(hire.work_date).toLocaleDateString('en-AU')}</div>
              <div><span className="text-xs uppercase font-bold text-slate-500 block">Start</span>{hire.start_time || '—'}</div>
              <div className="col-span-2"><span className="text-xs uppercase font-bold text-slate-500 block">Location</span>{hire.location || '—'}</div>
              <div className="col-span-2"><span className="text-xs uppercase font-bold text-slate-500 block">Description</span>{hire.description || '—'}</div>
              <div><span className="text-xs uppercase font-bold text-slate-500 block">Operator</span>{hire.operator_name || '—'}</div>
              <div><span className="text-xs uppercase font-bold text-slate-500 block">Dogman / Rigger</span>{(hire.dogman_names || []).map(d => d.name || d).join(', ') || '—'}</div>
              <div><span className="text-xs uppercase font-bold text-slate-500 block">Hourly Rate</span>{hire.hourly_rate ? `$${hire.hourly_rate}/hr` : '—'}</div>
              <div><span className="text-xs uppercase font-bold text-slate-500 block">Client Phone</span>{hire.client_phone || '—'}</div>
              {hire.clock_on_time && (
                <div><span className="text-xs uppercase font-bold text-slate-500 block">Clocked On</span>{new Date(hire.clock_on_time).toLocaleString('en-AU')}</div>
              )}
              {hire.clock_off_time && (
                <div><span className="text-xs uppercase font-bold text-slate-500 block">Clocked Off</span>{new Date(hire.clock_off_time).toLocaleString('en-AU')}</div>
              )}
              {hire.total_hours != null && (
                <div><span className="text-xs uppercase font-bold text-slate-500 block">Total Hours</span>{hire.total_hours}h</div>
              )}
              {hire.notes && (
                <div className="col-span-2"><span className="text-xs uppercase font-bold text-slate-500 block">Notes</span>{hire.notes}</div>
              )}
            </div>

            {/* Action buttons */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-2 border-t">
              {canStart && (
                <Button onClick={() => action('/clock-on', { successMsg: 'Started — clocked on' })} disabled={busy} data-testid="start-hire-btn">
                  ▶ Start Job
                </Button>
              )}
              {canFinish && (
                <Button onClick={() => action('/clock-off', { successMsg: 'Clocked off' })} disabled={busy} data-testid="finish-hire-btn">
                  ■ Finish Job
                </Button>
              )}
              {canComplete && (
                <Button variant="outline" onClick={() => action('/complete', { successMsg: 'Marked complete' })} disabled={busy} data-testid="complete-hire-btn">
                  ✓ Mark Complete
                </Button>
              )}
              {canEdit && hire.status !== 'completed' && (
                <Button variant="outline" onClick={() => setEditing(true)} data-testid="edit-hire-btn">
                  ✎ Edit
                </Button>
              )}
              <Button variant="outline" onClick={() => window.open(`${API}/general-hires/${hire.id}/pdf`, '_blank')} data-testid="hire-pdf-btn">
                ⬇ PDF
              </Button>
              {canDelete && (
                <Button variant="outline" className="text-red-600 hover:bg-red-50" onClick={onDelete} disabled={busy} data-testid="delete-hire-btn">
                  🗑 Delete
                </Button>
              )}
            </div>
          </>
        ) : (
          <>
            {/* Edit form */}
            <div className="space-y-3" data-testid="edit-hire-form">
              <Select
                label="Client"
                value={form.client_id}
                onChange={(e) => setForm(f => ({ ...f, client_id: e.target.value }))}
                options={[{ value: "", label: "Select client" }, ...clients.map(c => ({ value: c.id, label: c.name }))]}
              />
              <Input label="Site Location" value={form.location} onChange={(e) => setForm(f => ({ ...f, location: e.target.value }))} />
              <Textarea label="Description" value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} rows={2} />
              <div className="grid md:grid-cols-3 gap-3">
                <Input label="Date" type="date" value={form.work_date} onChange={(e) => setForm(f => ({ ...f, work_date: e.target.value }))} disabled={hire.status !== 'scheduled'} />
                <Input label="Start Time" type="time" value={form.start_time} onChange={(e) => setForm(f => ({ ...f, start_time: e.target.value }))} disabled={hire.status !== 'scheduled'} />
                <Input label="Hourly Rate ($)" type="number" value={form.hourly_rate} onChange={(e) => setForm(f => ({ ...f, hourly_rate: e.target.value }))} />
              </div>
              {hire.status === 'scheduled' && (
                <Select
                  label="Operator"
                  value={form.operator_id}
                  onChange={(e) => setForm(f => ({ ...f, operator_id: e.target.value }))}
                  options={[{ value: "", label: "Select operator" }, ...operators.map(u => ({ value: u.id, label: `${u.first_name} ${u.last_name}` }))]}
                />
              )}
              <Input label="Client Phone" value={form.client_phone} onChange={(e) => setForm(f => ({ ...f, client_phone: e.target.value }))} />
              <Textarea label="Notes" value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} />
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
                <Button onClick={onSaveEdit} disabled={busy} data-testid="save-edit-hire-btn">{busy ? 'Saving...' : 'Save Changes'}</Button>
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};


// Machine Modal with Documents and Photos
const DaybookReceiptsSection = ({ machineId, usage, onRefresh }) => {
  const [uploading, setUploading] = React.useState(false);
  const [showForm, setShowForm] = React.useState(false);
  const [form, setForm] = React.useState({
    receipt_type: 'daybook',
    photo_url: '',
    amount: '',
    reference: '',
    notes: '',
    date: localTodayISO()
  });
  const receipts = usage?.daybook_receipts || [];

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const r = await uploadReceipt(file);
      setForm((f) => ({ ...f, photo_url: r.url }));
      toast.success("Receipt uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Receipt upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.photo_url) {
      alert('Snap a photo of the receipt first, mate.');
      return;
    }
    setUploading(true);
    try {
      const payload = {
        ...form,
        amount: form.amount === '' ? null : parseFloat(form.amount)
      };
      await axios.post(`${API}/machines/${machineId}/usage/${usage.id}/daybook-receipt`, payload);
      setForm({
        receipt_type: 'daybook',
        photo_url: '',
        amount: '',
        reference: '',
        notes: '',
        date: localTodayISO()
      });
      setShowForm(false);
      await onRefresh?.();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save receipt');
    } finally {
      setUploading(false);
    }
  };

  const remove = async (receiptId) => {
    if (!window.confirm('Delete this receipt?')) return;
    try {
      await axios.delete(`${API}/machines/${machineId}/usage/${usage.id}/daybook-receipt/${receiptId}`);
      await onRefresh?.();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete receipt');
    }
  };

  const typeLabel = {
    daybook: 'Daybook Docket',
    fuel: 'Fuel',
    toll: 'Toll',
    other: 'Other'
  };

  return (
    <div className="mt-4 pt-4 border-t border-green-200" data-testid="daybook-receipts-section">
      <div className="flex items-center justify-between mb-3">
        <h5 className="font-bold text-sm uppercase">Daybook / Vehicle Receipts ({receipts.length})</h5>
        {!showForm && (
          <Button size="sm" variant="secondary" onClick={() => setShowForm(true)} data-testid="add-daybook-receipt-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Receipt
          </Button>
        )}
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-white border border-green-200 rounded-lg p-3 mb-3 space-y-3" data-testid="daybook-receipt-form">
          <div className="grid md:grid-cols-3 gap-3">
            <Select
              label="Type"
              value={form.receipt_type}
              onChange={(e) => setForm({ ...form, receipt_type: e.target.value })}
              options={[
                { value: 'daybook', label: 'Daybook Docket' },
                { value: 'fuel', label: 'Fuel' },
                { value: 'toll', label: 'Toll' },
                { value: 'other', label: 'Other' }
              ]}
              testId="daybook-receipt-type"
            />
            <Input
              label="Amount ($)"
              type="number"
              step="0.01"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              placeholder="0.00"
              testId="daybook-receipt-amount"
            />
            <Input
              label="Reference / Docket #"
              value={form.reference}
              onChange={(e) => setForm({ ...form, reference: e.target.value })}
              placeholder="Optional"
              testId="daybook-receipt-reference"
            />
          </div>
          <Input
            label="Date"
            type="date"
            value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
            testId="daybook-receipt-date"
          />
          <Textarea
            label="Notes"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="Any extra detail..."
            rows={2}
          />
          <div>
            <label className="text-xs font-bold uppercase text-slate-600 block mb-1">Receipt Photo</label>
            {form.photo_url ? (
              <div className="flex items-center gap-3">
                <img src={form.photo_url} alt="receipt" className="w-20 h-20 object-cover rounded border" />
                <Button type="button" variant="ghost" size="sm" onClick={() => setForm({ ...form, photo_url: '' })}>
                  Change
                </Button>
              </div>
            ) : (
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={(e) => handleFile(e.target.files?.[0])}
                className="w-full text-sm"
                data-testid="daybook-receipt-file"
              />
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button type="submit" size="sm" disabled={uploading} data-testid="daybook-receipt-submit">
              {uploading ? <Spinner size="sm" /> : 'Save Receipt'}
            </Button>
          </div>
        </form>
      )}

      {receipts.length > 0 ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {receipts.map((r) => (
            <div key={r.id} className="bg-white border border-slate-200 rounded-lg p-2 text-xs" data-testid={`daybook-receipt-${r.id}`}>
              <img
                src={r.photo_url}
                alt={r.reference || r.receipt_type}
                className="w-full h-28 object-cover rounded mb-2 cursor-pointer"
                onClick={() => window.open(r.photo_url, '_blank')}
              />
              <div className="flex items-center justify-between mb-1">
                <Badge variant={r.receipt_type === 'daybook' ? 'warning' : 'info'}>{typeLabel[r.receipt_type] || r.receipt_type}</Badge>
                {r.amount != null && <span className="font-bold text-slate-700">${Number(r.amount).toFixed(2)}</span>}
              </div>
              {r.reference && <p className="text-slate-600 truncate"><strong>#</strong> {r.reference}</p>}
              <p className="text-slate-500">{new Date(r.date || r.uploaded_at).toLocaleDateString('en-AU')}</p>
              {r.notes && <p className="text-slate-400 truncate">{r.notes}</p>}
              <div className="flex items-center justify-between mt-1">
                <span className="text-slate-400 text-[10px]">By {r.uploaded_by_name}</span>
                <button type="button" onClick={() => remove(r.id)} className="text-red-500 text-[10px] hover:underline">Delete</button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        !showForm && <p className="text-xs text-slate-400 italic">No receipts logged yet.</p>
      )}
    </div>
  );
};

const MachineModal = ({ isOpen, onClose, machine, onSave, canEdit }) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('details');
  const [formData, setFormData] = useState({
    name: "",
    machine_type: "tower_crane",
    registration_number: "",
    manufacturer: "",
    model: "",
    year: "",
    capacity: "",
    description: "",
    status: "available",
    current_km: "",
    current_hours: ""
  });
  const [documents, setDocuments] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [assignedJobs, setAssignedJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newDoc, setNewDoc] = useState({ name: "", document_type: "safety_cert", description: "", expiry_date: "" });
  const [newPhoto, setNewPhoto] = useState({ title: "", photo_type: "display", description: "", file_url: "" });
  
  // Usage tracking state
  const [usageHistory, setUsageHistory] = useState([]);
  const [activeUsage, setActiveUsage] = useState(null);
  const [deploymentStatus, setDeploymentStatus] = useState(null);
  const [showNewUsage, setShowNewUsage] = useState(false);
  const [showCompleteUsage, setShowCompleteUsage] = useState(false);
  const [showDryHire, setShowDryHire] = useState(false);
  const [editingUsageId, setEditingUsageId] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [machineConflict, setMachineConflict] = useState(null);
  const [readingMismatch, setReadingMismatch] = useState(null);

  // Pre-fill last odometer reading whenever the "New Usage" form opens
  useEffect(() => {
    if (!showNewUsage || !machine?.id) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/machines/${machine.id}/last-readings`);
        const data = r.data || {};
        setNewUsage((prev) => ({
          ...prev,
          starting_hours: prev.starting_hours || (data.last_hours != null ? String(data.last_hours) : ""),
          starting_kms: prev.starting_kms || (data.last_kms != null ? String(data.last_kms) : ""),
          // Keep a copy of expected for the reconcile prompt
        }));
      } catch (e) {
        /* noop — operator can type manually */
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showNewUsage, machine?.id]);
  const [newUsage, setNewUsage] = useState({
    deployment_type: "quoted_job",
    job_id: "",
    client_name: "",
    location: "",
    start_date: new Date().toISOString().split('T')[0],
    expected_end_date: "",
    rate_type: "hourly",
    notes: "",
    starting_hours: "",
    starting_kms: "",
    operator_id: "",
    dogman_ids: [],
    no_dogman_required: false
  });
  const [completeUsageData, setCompleteUsageData] = useState({
    actual_end_date: new Date().toISOString().split('T')[0],
    receipt_photo_url: null,
    client_signature: null,
    machine_location: "back_to_yard",
    other_location_address: "",
    damages_or_faults: "",
    ending_hours: "",
    ending_kms: "",
    notes: ""
  });
  const [newHoursLog, setNewHoursLog] = useState({
    date: new Date().toISOString().split('T')[0],
    hours: 0,
    notes: ""
  });

  // Hire Out tab state
  const [hireOutHistory, setHireOutHistory] = useState([]);
  const [clients, setClients] = useState([]);
  const [showNewHireOut, setShowNewHireOut] = useState(false);
  const [newHireOut, setNewHireOut] = useState({
    client_id: "",
    start_date: new Date().toISOString().split('T')[0],
    start_time: "07:00",
    expected_return_date: "",
    rate_type: "daily",
    rate_amount: "",
    delivery_method: "client_pickup",
    hire_location: "",
    hours_out: "",
    km_out: "",
    fuel_level_out: 100,
    condition_notes_out: "",
    client_provides_operator: false,
    operator_name: "",
    operator_phone: "",
    notes: ""
  });

  const canLogHours = ['admin', 'manager', 'supervisor', 'office_staff', 'crane_operator'].includes(user?.role);
  const canManageUsage = ['admin', 'manager', 'office_staff', 'supervisor'].includes(user?.role);
  const canHireOut = ['admin', 'manager', 'office_staff'].includes(user?.role);

  useEffect(() => {
    if (machine) {
      setFormData({
        name: machine.name || "",
        machine_type: machine.machine_type || "tower_crane",
        registration_number: machine.registration_number || "",
        manufacturer: machine.manufacturer || "",
        model: machine.model || "",
        year: machine.year || "",
        capacity: machine.capacity || "",
        description: machine.description || "",
        status: machine.status || "available",
        current_km: machine.current_km != null ? String(machine.current_km) : "",
        current_hours: machine.current_hours != null ? String(machine.current_hours) : ""
      });
      setAssignedJobs(machine.assigned_jobs || []);
      fetchMachineData();
    } else {
      setFormData({
        name: "",
        machine_type: "tower_crane",
        registration_number: "",
        manufacturer: "",
        model: "",
        year: "",
        capacity: "",
        description: "",
        status: "available",
        current_km: "",
        current_hours: ""
      });
      setDocuments([]);
      setPhotos([]);
      setAssignedJobs([]);
    }
    fetchJobs();
  }, [machine]);

  const fetchMachineData = async () => {
    if (!machine) return;
    try {
      const [docsRes, photosRes, usageRes, hireRes, clientsRes, employeesRes, deployRes] = await Promise.all([
        axios.get(`${API}/machines/${machine.id}/documents`),
        axios.get(`${API}/machines/${machine.id}/photos`),
        axios.get(`${API}/machines/${machine.id}/usage`),
        axios.get(`${API}/machines/${machine.id}/hire-history`).catch(() => ({ data: { hires: [] } })),
        axios.get(`${API}/clients`).catch(() => ({ data: [] })),
        axios.get(`${API}/users`).catch(() => ({ data: [] })),
        axios.get(`${API}/machines/${machine.id}/deployment-status`).catch(() => ({ data: null }))
      ]);
      setDocuments(docsRes.data);
      setPhotos(photosRes.data);
      setUsageHistory(usageRes.data);
      setHireOutHistory(hireRes.data.hires || []);
      setClients(clientsRes.data || []);
      setEmployees(employeesRes.data || []);
      setDeploymentStatus(deployRes.data);
      // Find active usage
      const active = usageRes.data.find(u => u.status === 'active');
      setActiveUsage(active || null);
      // Pre-fill machine readings for new hire out
      setNewHireOut(prev => ({
        ...prev,
        hours_out: machine.current_hours || "",
        km_out: machine.current_km || ""
      }));
    } catch (e) {}
  };

  const fetchJobs = async () => {
    try {
      const res = await axios.get(`${API}/jobs`);
      setJobs(res.data);
    } catch (e) {}
  };

  // Usage tracking handlers
  const handleStartUsage = async (e) => {
    e.preventDefault();
    const dt = newUsage.deployment_type || "quoted_job";
    const isQuoted = dt === "quoted_job";
    if (isQuoted) {
      if (!newUsage.job_id) {
        alert("Please pick which quoted company job this machine is being deployed onto.");
        return;
      }
    } else {
      if (!newUsage.client_name) {
        alert("Please enter a client name for the general hire");
        return;
      }
    }
    if (!newUsage.operator_id) {
      alert("Mate, you've got to pick an operator before deploying this machine.");
      return;
    }
    try {
      const usageData = {
        ...newUsage,
        client_name: isQuoted ? (newUsage.client_name || "Internal") : newUsage.client_name,
        starting_hours: newUsage.starting_hours ? parseFloat(newUsage.starting_hours) : null,
        starting_kms: newUsage.starting_kms ? parseFloat(newUsage.starting_kms) : null
      };
      await axios.post(`${API}/machines/${machine.id}/usage`, usageData);
      alert("Machine usage tracking started");
      setShowNewUsage(false);
      setNewUsage({
        deployment_type: "quoted_job",
        job_id: "",
        client_name: "",
        location: "",
        start_date: new Date().toISOString().split('T')[0],
        expected_end_date: "",
        rate_type: "hourly",
        notes: "",
        starting_hours: "",
        starting_kms: "",
        operator_id: "",
        dogman_ids: [],
        no_dogman_required: false
      });
      fetchMachineData();
      onSave();
    } catch (e) {
      const conflict = parseMachineConflict(e);
      if (conflict) {
        setMachineConflict(conflict);
        return;
      }
      // Reading mismatch — odometer differs from machine profile
      const d = e.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "READING_MISMATCH") {
        setReadingMismatch(d);
        return;
      }
      alert(e.response?.data?.detail || "Failed to start usage tracking");
    }
  };

  const handleLogHours = async (usageId) => {
    if (newHoursLog.hours <= 0) {
      alert("Please enter valid hours");
      return;
    }
    try {
      await axios.post(`${API}/machines/${machine.id}/usage/${usageId}/log-hours`, newHoursLog);
      alert("Hours logged successfully");
      setNewHoursLog({
        date: new Date().toISOString().split('T')[0],
        hours: 0,
        notes: ""
      });
      fetchMachineData();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to log hours");
    }
  };

  const handleCompleteUsage = async (usageId) => {
    // Reset and show the completion modal
    setCompleteUsageData({
      actual_end_date: new Date().toISOString().split('T')[0],
      receipt_photo_url: null,
      client_signature: null,
      machine_location: "back_to_yard",
      other_location_address: "",
      damages_or_faults: "",
      ending_hours: activeUsage?.starting_hours ? "" : "",
      ending_kms: activeUsage?.starting_kms ? "" : "",
      notes: ""
    });
    setShowCompleteUsage(true);
  };

  const handleSubmitCompleteUsage = async (e) => {
    e.preventDefault();
    if (!activeUsage) return;
    
    // Validate other location if selected
    if (completeUsageData.machine_location === "other" && !completeUsageData.other_location_address) {
      alert("Please enter the location address");
      return;
    }
    
    try {
      const completionData = {
        ...completeUsageData,
        ending_hours: completeUsageData.ending_hours ? parseFloat(completeUsageData.ending_hours) : null,
        ending_kms: completeUsageData.ending_kms ? parseFloat(completeUsageData.ending_kms) : null
      };
      await axios.post(`${API}/machines/${machine.id}/usage/${activeUsage.id}/complete`, completionData);
      alert("Machine usage completed successfully");
      setShowCompleteUsage(false);
      fetchMachineData();
      onSave();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to complete usage");
    }
  };

  const handleReceiptUpload = async (file) => {
    if (!file) return;
    try {
      toast.info("Uploading receipt...");
      const r = await uploadReceipt(file);
      setCompleteUsageData({ ...completeUsageData, receipt_photo_url: r.url });
      toast.success("Receipt uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Receipt upload failed");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const dataToSend = {
        ...formData,
        year: formData.year ? parseInt(formData.year) : null,
        current_km: formData.current_km === "" ? null : parseFloat(formData.current_km),
        current_hours: formData.current_hours === "" ? null : parseFloat(formData.current_hours),
      };
      if (machine) {
        await axios.put(`${API}/machines/${machine.id}`, dataToSend);
      } else {
        await axios.post(`${API}/machines`, dataToSend);
      }
      onSave();
      onClose();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to save machine");
    } finally {
      setLoading(false);
    }
  };

  const handleAssignJobs = async () => {
    try {
      await axios.post(`${API}/machines/${machine.id}/assign-jobs`, { job_ids: assignedJobs });
      alert("Jobs assigned successfully");
      onSave();
    } catch (e) {
      alert("Failed to assign jobs");
    }
  };

  const handleAddDocument = async (e) => {
    e.preventDefault();
    if (!newDoc.file_url) { alert("Please upload a document file first"); return; }
    try {
      await axios.post(`${API}/machines/${machine.id}/documents`, newDoc);
      setNewDoc({ name: "", document_type: "safety_cert", description: "", expiry_date: "", file_url: "" });
      fetchMachineData();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to add document");
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm("Delete this document? This can't be undone.")) return;
    try {
      await axios.delete(`${API}/machines/${machine.id}/documents/${docId}`);
      fetchMachineData();
    } catch (e) {
      alert("Failed to delete document");
    }
  };

  const handleAddPhoto = async (e) => {
    e.preventDefault();
    if (!newPhoto.file_url) {
      alert("Please enter a photo URL");
      return;
    }
    try {
      await axios.post(`${API}/machines/${machine.id}/photos`, newPhoto);
      setNewPhoto({ title: "", photo_type: "display", description: "", file_url: "" });
      fetchMachineData();
    } catch (e) {
      alert("Failed to add photo");
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await axios.delete(`${API}/machines/${machine.id}/photos/${photoId}`);
      fetchMachineData();
    } catch (e) {
      alert("Failed to delete photo");
    }
  };

  const handleDeleteMachine = async () => {
    if (!window.confirm(`Are you sure you want to delete "${machine.name}"? This will also delete all associated documents and photos. This action cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/machines/${machine.id}`);
      alert("Machine deleted successfully");
      onSave();
      onClose();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to delete machine");
    }
  };

  const handleSetStatus = async (newStatus, confirmMessage) => {
    if (!window.confirm(confirmMessage)) return;
    try {
      await axios.put(`${API}/machines/${machine.id}`, { status: newStatus });
      alert(`Machine status updated to ${newStatus.replace('_', ' ')}`);
      onSave();
      onClose();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to update status");
    }
  };

  const handleMarkAvailable = () => handleSetStatus("available", "Mark this machine as available?");
  const handleMarkInUse = () => handleSetStatus("in_use", "Mark this machine as in use?");
  const handleMarkMaintenance = () => handleSetStatus("maintenance", "Send this machine to maintenance?");
  const handleMarkOutOfService = () => handleSetStatus("out_of_service", "Mark this machine as out of service? It will not be available for jobs.");

  const docTypeLabels = {
    safety_cert: "Safety Certificate",
    inspection: "Inspection Report",
    manual: "Operator Manual",
    registration: "Registration",
    insurance: "Insurance",
    maintenance: "Maintenance Record",
    other: "Other"
  };

  const photoTypeLabels = {
    display: "Display Photo",
    damage: "Damage Photo",
    inspection: "Inspection Photo",
    evidence: "Evidence",
    job_photo: "Job Photo"
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={machine ? machine.name : "Add Machine"} size="xl">
      {/* Deployment Status Panel — visible at top whenever machine is deployed */}
      {machine && deploymentStatus && ["in_use", "hired_out", "out_on_hire", "maintenance"].includes(deploymentStatus.status) && (
        <Card
          className={`mb-4 border-2 ${
            deploymentStatus.status === 'in_use' ? 'bg-green-50 border-green-300' :
            (deploymentStatus.status === 'hired_out' || deploymentStatus.status === 'out_on_hire') ? 'bg-blue-50 border-blue-300' :
            'bg-amber-50 border-amber-300'
          }`}
          data-testid="machine-deployment-status-panel"
        >
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Badge variant={deploymentStatus.status === 'in_use' ? 'success' : deploymentStatus.status === 'maintenance' ? 'warning' : 'info'}>
                {deploymentStatus.status === 'in_use' ? 'IN USE' :
                 (deploymentStatus.status === 'hired_out' || deploymentStatus.status === 'out_on_hire') ? 'OUT ON HIRE' :
                 'MAINTENANCE'}
              </Badge>
              {deploymentStatus.details?.overdue && <Badge variant="error">OVERDUE</Badge>}
            </div>
            <span className="text-xs text-slate-500">
              {deploymentStatus.current_km != null && <>KM: {deploymentStatus.current_km}</>}
              {deploymentStatus.current_hours != null && <> · Hrs: {deploymentStatus.current_hours}</>}
            </span>
          </div>

          {deploymentStatus.deployment_type === 'in_use' && (
            <div className="grid md:grid-cols-2 gap-3 text-sm">
              <div><span className="text-slate-500">Job:</span> <strong>{deploymentStatus.details.job_title || '-'}</strong></div>
              <div><span className="text-slate-500">Client:</span> <strong>{deploymentStatus.details.client_name || '-'}</strong></div>
              <div><span className="text-slate-500">Location:</span> {deploymentStatus.details.job_location || '-'}</div>
              <div><span className="text-slate-500">Started:</span> {deploymentStatus.details.start_date || '-'}</div>
              <div><span className="text-slate-500">Expected End:</span> {deploymentStatus.details.expected_end_date || '—'}</div>
              <div><span className="text-slate-500">Rate Type:</span> {deploymentStatus.details.rate_type || '-'}</div>
              <div className="md:col-span-2 pt-2 border-t border-green-200">
                <span className="text-slate-500">Operator:</span>{' '}
                <strong>{deploymentStatus.details.operator?.name || 'Unassigned'}</strong>
                {deploymentStatus.details.operator?.role && <span className="text-xs text-slate-500"> ({deploymentStatus.details.operator.role})</span>}
              </div>
              {deploymentStatus.details.operator_clock_in ? (
                <div className="md:col-span-2 text-green-700 font-semibold">
                  ✓ Operator clocked in at {new Date(deploymentStatus.details.operator_clock_in).toLocaleString('en-AU', { weekday: 'short', hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' })}
                  {(() => {
                    const diff = (new Date() - new Date(deploymentStatus.details.operator_clock_in)) / 3600000;
                    return ` · ${diff.toFixed(1)}h on machine`;
                  })()}
                </div>
              ) : (
                <div className="md:col-span-2 text-slate-500 italic">Operator not currently clocked-in</div>
              )}
              {deploymentStatus.details.dogman_names?.length > 0 && (
                <div className="md:col-span-2">
                  <span className="text-slate-500">Dogman:</span> {deploymentStatus.details.dogman_names.join(', ')}
                </div>
              )}
              <div className="md:col-span-2 text-xs text-slate-500">
                Hours logged on this deployment: <strong>{deploymentStatus.details.total_hours_so_far || 0}h</strong> over {deploymentStatus.details.total_days_so_far || 0} day(s)
              </div>
            </div>
          )}

          {deploymentStatus.deployment_type === 'hired_out' && (
            <div className="grid md:grid-cols-2 gap-3 text-sm">
              <div><span className="text-slate-500">Hirer:</span> <strong>{deploymentStatus.details.client_name || '-'}</strong></div>
              <div><span className="text-slate-500">Hire Start:</span> {deploymentStatus.details.hire_start_date || '-'}</div>
              <div><span className="text-slate-500">Expected Return:</span>{' '}
                <strong>{deploymentStatus.details.expected_return_date || '-'}</strong>
                {deploymentStatus.details.expected_return_time && <> at {deploymentStatus.details.expected_return_time}</>}
              </div>
              <div><span className="text-slate-500">Rate:</span> ${deploymentStatus.details.rate_amount || 0} / {deploymentStatus.details.rate_type || '-'}</div>
              <div><span className="text-slate-500">Checked out:</span> {deploymentStatus.details.checked_out_at ? new Date(deploymentStatus.details.checked_out_at).toLocaleString('en-AU') : '-'}</div>
              <div><span className="text-slate-500">Hours/KM at checkout:</span> {deploymentStatus.details.hours_out || '-'}h · {deploymentStatus.details.km_out || '-'}km</div>
              {deploymentStatus.details.client_provides_operator && deploymentStatus.details.hire_operator && (
                <div className="md:col-span-2 pt-2 border-t border-blue-200">
                  <span className="text-slate-500">Client operator:</span>{' '}
                  <strong>{deploymentStatus.details.hire_operator.name}</strong>
                  {deploymentStatus.details.hire_operator.phone && <> · {deploymentStatus.details.hire_operator.phone}</>}
                </div>
              )}
              {deploymentStatus.details.special_conditions && (
                <div className="md:col-span-2 text-amber-700 italic text-xs">⚠ {deploymentStatus.details.special_conditions}</div>
              )}
            </div>
          )}

          {deploymentStatus.deployment_type === 'maintenance' && (
            <div className="space-y-2 text-sm">
              <div><span className="text-slate-500">Reason:</span> <strong>{deploymentStatus.details.maintenance_reason || '(not specified)'}</strong></div>
              {deploymentStatus.details.maintenance_notes && <div><span className="text-slate-500">Notes:</span> {deploymentStatus.details.maintenance_notes}</div>}
              {deploymentStatus.details.expected_return_to_service && (
                <div><span className="text-slate-500">Expected back in service:</span> <strong>{deploymentStatus.details.expected_return_to_service}</strong></div>
              )}
              {deploymentStatus.details.last_maintenance_doc && (
                <div className="pt-2 border-t border-amber-200 text-xs text-slate-600">
                  Latest maintenance record: <strong>{deploymentStatus.details.last_maintenance_doc.name}</strong> ({(deploymentStatus.details.last_maintenance_doc.created_at || '').slice(0, 10)})
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      {machine && (
        <div className="flex gap-2 mb-6 border-b-2 border-slate-200 pb-4 overflow-x-auto">
          {['details', 'activity', 'schedule', 'hire-out', 'documents', 'photos', 'jobs', 'registry'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-md font-semibold text-sm uppercase transition-colors whitespace-nowrap ${
                activeTab === tab ? 'bg-orange-500 text-slate-900' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              data-testid={`machine-tab-${tab}`}
            >
              {tab === 'hire-out' ? 'Hire Out' : tab === 'schedule' ? 'Schedule' : tab === 'registry' ? 'Registry' : tab === 'activity' ? 'Activity' : tab}
            </button>
          ))}
        </div>
      )}

      {/* Details Tab */}
      {activeTab === 'details' && (
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <Input
              label="Machine Name"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              required
              disabled={!canEdit}
            />
            <Select
              label="Machine Type"
              value={formData.machine_type}
              onChange={(e) => setFormData({...formData, machine_type: e.target.value})}
              options={[
                { value: "tower_crane", label: "Tower Crane" },
                { value: "mobile_crane", label: "Mobile Crane" },
                { value: "crawler_crane", label: "Crawler Crane" },
                { value: "overhead_crane", label: "Overhead Crane" },
                { value: "rigging_equipment", label: "Rigging Equipment" },
                { value: "excavator", label: "Excavator" },
                { value: "loader", label: "Loader" },
                { value: "truck", label: "Truck" },
                { value: "tipper", label: "Tipper Truck" },
                { value: "water_truck", label: "Water Truck" },
                { value: "ute", label: "Ute" },
                { value: "conveyor", label: "Conveyor" },
                { value: "ewp", label: "EWP (Elevating Work Platform)" },
                { value: "boom_lift", label: "Boom Lift" },
                { value: "scissor_lift", label: "Scissor Lift" },
                { value: "telehandler", label: "Telehandler" },
                { value: "forklift", label: "Forklift" },
                { value: "dozer", label: "Dozer" },
                { value: "grader", label: "Grader" },
                { value: "skid_steer", label: "Skid Steer" },
                { value: "roller", label: "Roller" },
                { value: "generator", label: "Generator" },
                { value: "compressor", label: "Compressor" },
                { value: "other", label: "Other" }
              ]}
              disabled={!canEdit}
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <Input
              label="Registration Number"
              value={formData.registration_number}
              onChange={(e) => setFormData({...formData, registration_number: e.target.value})}
              disabled={!canEdit}
            />
            <Input
              label="Capacity"
              value={formData.capacity}
              onChange={(e) => setFormData({...formData, capacity: e.target.value})}
              placeholder="e.g., 100 tonnes"
              disabled={!canEdit}
            />
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <Input
              label="Manufacturer"
              value={formData.manufacturer}
              onChange={(e) => setFormData({...formData, manufacturer: e.target.value})}
              disabled={!canEdit}
            />
            <Input
              label="Model"
              value={formData.model}
              onChange={(e) => setFormData({...formData, model: e.target.value})}
              disabled={!canEdit}
            />
            <Input
              label="Year"
              type="number"
              value={formData.year}
              onChange={(e) => setFormData({...formData, year: e.target.value})}
              disabled={!canEdit}
            />
          </div>

          <Textarea
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            disabled={!canEdit}
          />

          {/* Pre-loaded odometer + hour-meter so workers don't need to retype on clock-in */}
          <div className="grid md:grid-cols-2 gap-4">
            <Input
              label="Current KM (odometer reading)"
              type="number"
              step="0.1"
              value={formData.current_km}
              onChange={(e) => setFormData({...formData, current_km: e.target.value})}
              placeholder="e.g. 52340"
              disabled={!canEdit}
              data-testid="machine-current-km"
            />
            <Input
              label="Current Hours (engine hours)"
              type="number"
              step="0.1"
              value={formData.current_hours}
              onChange={(e) => setFormData({...formData, current_hours: e.target.value})}
              placeholder="e.g. 1240.5"
              disabled={!canEdit}
              data-testid="machine-current-hours"
            />
          </div>
          <p className="text-xs text-slate-500 -mt-2">
            These values pre-fill on machine clock-in so workers only need to confirm or adjust.
          </p>

          <Select
            label="Status"
            value={formData.status}
            onChange={(e) => setFormData({...formData, status: e.target.value})}
            options={[
              { value: "available", label: "Available" },
              { value: "in_use", label: "In Use" },
              { value: "maintenance", label: "Maintenance" },
              { value: "out_of_service", label: "Out of Service" }
            ]}
            disabled={!canEdit}
          />

          {canEdit && (
            <div className="flex flex-wrap gap-4">
              <Button type="submit" disabled={loading}>
                {loading ? <Spinner size="sm" /> : (machine ? "Update Machine" : "Add Machine")}
              </Button>
              <Button type="button" variant="ghost" onClick={onClose}>Close</Button>
            </div>
          )}

          {/* Machine Actions - only show for existing machines */}
          {machine && canEdit && (
            <div className="mt-6 pt-6 border-t-2 border-slate-200">
              <h4 className="font-bold uppercase text-slate-500 mb-4">Quick Actions</h4>
              <div className="flex flex-wrap gap-3">
                {machine.status !== 'available' && (
                  <Button type="button" variant="secondary" size="sm" onClick={handleMarkAvailable} data-testid="mark-available-btn">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Mark Available
                  </Button>
                )}
                {machine.status !== 'in_use' && (
                  <Button type="button" variant="outline" size="sm" onClick={handleMarkInUse} data-testid="mark-inuse-btn">
                    <Play className="w-4 h-4 mr-2" />
                    Mark In Use
                  </Button>
                )}
                {machine.status !== 'maintenance' && (
                  <Button type="button" variant="outline" size="sm" onClick={handleMarkMaintenance} data-testid="mark-maintenance-btn">
                    <Wrench className="w-4 h-4 mr-2" />
                    Send to Maintenance
                  </Button>
                )}
                {machine.status !== 'out_of_service' && (
                  <Button type="button" variant="destructive" size="sm" onClick={handleMarkOutOfService} data-testid="mark-outofservice-btn">
                    <X className="w-4 h-4 mr-2" />
                    Out of Service
                  </Button>
                )}
              </div>

              {/* Delete Machine */}
              <div className="mt-6 pt-4 border-t border-slate-200">
                <Button type="button" variant="destructive" onClick={handleDeleteMachine} data-testid="delete-machine-btn">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Machine
                </Button>
                <p className="text-xs text-slate-500 mt-2">This will permanently delete the machine and all its documents/photos.</p>
              </div>
            </div>
          )}
        </form>
      )}

      {/* Required Tickets section — shown in details tab when not creating */}
      {activeTab === 'details' && machine && (
        <MachineRequiredTicketsEditor
          machine={machine}
          currentUser={user}
          onSaved={fetchMachineData}
        />
      )}

      {/* Activity Tab - unified Deploy / Complete / Timeline / History */}
      {activeTab === 'activity' && machine && (
        <div className="space-y-6">
          {/* Active Usage Banner */}
          {activeUsage && (
            <Card className="bg-green-50 border-green-200">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <Badge variant="success" className="mb-2">Currently Active</Badge>
                  <h4 className="font-bold text-lg">{activeUsage.client_name}</h4>
                  <p className="text-sm text-slate-600">
                    {activeUsage.location && <span>{activeUsage.location} • </span>}
                    Started: {new Date(activeUsage.start_date).toLocaleDateString()}
                  </p>
                  {activeUsage.job_title && (
                    <p className="text-sm text-blue-600">Job: {activeUsage.job_title}</p>
                  )}
                  <div className="flex items-center gap-4 mt-2">
                    <span className="text-2xl font-bold text-orange-600">{activeUsage.total_hours || 0}h</span>
                    <span className="text-sm text-slate-500">{activeUsage.total_days || 0} days logged</span>
                  </div>
                </div>
                {canManageUsage && (
                  <Button size="sm" variant="secondary" onClick={() => handleCompleteUsage(activeUsage.id)}>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Complete
                  </Button>
                )}
              </div>

              {/* Log Hours Form */}
              {canLogHours && (
                <div className="mt-4 pt-4 border-t border-green-200">
                  <h5 className="font-bold text-sm uppercase mb-3">Log Daily Hours</h5>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                    <div className="flex flex-col gap-1.5">
                      <label className="text-sm font-bold uppercase text-slate-600">Date</label>
                      <input
                        type="date"
                        value={newHoursLog.date}
                        onChange={(e) => setNewHoursLog({...newHoursLog, date: e.target.value})}
                        className="h-12 bg-white border-2 border-slate-200 focus:border-orange-500 focus:outline-none rounded-md px-3"
                      />
                    </div>
                    <Input
                      label="Hours"
                      type="number"
                      step="0.5"
                      min="0"
                      max="24"
                      value={newHoursLog.hours}
                      onChange={(e) => setNewHoursLog({...newHoursLog, hours: parseFloat(e.target.value) || 0})}
                    />
                    <Input
                      label="Notes (optional)"
                      value={newHoursLog.notes}
                      onChange={(e) => setNewHoursLog({...newHoursLog, notes: e.target.value})}
                      placeholder="What was done"
                    />
                    <Button onClick={() => handleLogHours(activeUsage.id)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Log
                    </Button>
                  </div>
                </div>
              )}

              {/* Daily Logs Table */}
              {activeUsage.daily_logs && activeUsage.daily_logs.length > 0 && (
                <div className="mt-4 pt-4 border-t border-green-200">
                  <h5 className="font-bold text-sm uppercase mb-3">Daily Log</h5>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-green-100">
                        <tr>
                          <th className="text-left p-2 font-bold">Date</th>
                          <th className="text-right p-2 font-bold">Hours</th>
                          <th className="text-left p-2 font-bold">Notes</th>
                          <th className="text-left p-2 font-bold">Logged By</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activeUsage.daily_logs.map((log, idx) => (
                          <tr key={idx} className="border-b border-green-100">
                            <td className="p-2">{new Date(log.date).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })}</td>
                            <td className="p-2 text-right font-bold text-orange-600">{log.hours}h</td>
                            <td className="p-2 text-slate-600">{log.notes || '-'}</td>
                            <td className="p-2 text-slate-500 text-xs">{log.logged_by_name}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Daybook / Vehicle Receipts */}
              <DaybookReceiptsSection
                machineId={machine.id}
                usage={activeUsage}
                onRefresh={fetchMachineData}
              />
            </Card>
          )}

          {/* Start New Usage */}
          {!activeUsage && canManageUsage && (
            <>
              {!showNewUsage ? (
                <div className="text-center py-8">
                  <Truck className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p className="text-slate-500 mb-4">No active usage tracking</p>
                  <Button onClick={() => setShowNewUsage(true)}>
                    <Play className="w-4 h-4 mr-2" />
                    Start Usage Tracking
                  </Button>
                </div>
              ) : (
                <Card className="bg-blue-50 border-blue-200">
                  <h4 className="font-bold uppercase mb-4">Start Machine Usage</h4>
                  <form onSubmit={handleStartUsage} className="space-y-4">
                    {/* Deployment type toggle */}
                    <div className="bg-white border-2 border-slate-200 rounded-lg p-2 grid grid-cols-2 gap-1" data-testid="deployment-type-toggle">
                      <button
                        type="button"
                        onClick={() => setNewUsage((u) => ({ ...u, deployment_type: "quoted_job", client_name: "" }))}
                        className={`p-3 rounded text-sm font-bold uppercase transition-colors ${
                          (newUsage.deployment_type || "quoted_job") === "quoted_job"
                            ? "bg-orange-500 text-white"
                            : "bg-slate-50 text-slate-500 hover:bg-slate-100"
                        }`}
                        data-testid="deploy-quoted-job"
                      >
                        Quoted Job
                      </button>
                      <button
                        type="button"
                        onClick={() => setNewUsage((u) => ({ ...u, deployment_type: "general_hire", job_id: "" }))}
                        className={`p-3 rounded text-sm font-bold uppercase transition-colors ${
                          newUsage.deployment_type === "general_hire"
                            ? "bg-orange-500 text-white"
                            : "bg-slate-50 text-slate-500 hover:bg-slate-100"
                        }`}
                        data-testid="deploy-general-hire"
                      >
                        General Hire
                      </button>
                    </div>
                    <p className="text-xs text-slate-500 -mt-2">
                      <strong>Quoted Job:</strong> company's own job — machine usually stays on site for days/weeks.<br />
                      <strong>General Hire:</strong> client hire — operator signs a daybook each day on site.
                    </p>

                    <div className="grid md:grid-cols-2 gap-4">
                      {(newUsage.deployment_type || "quoted_job") === "quoted_job" ? (
                        <Select
                          label="Quoted Job *"
                          value={newUsage.job_id}
                          onChange={(e) => {
                            const j = jobs.find((x) => x.id === e.target.value);
                            setNewUsage({
                              ...newUsage,
                              job_id: e.target.value,
                              client_name: j?.client_name || "Internal",
                              location: j?.site_address || j?.location || newUsage.location,
                            });
                          }}
                          required
                          options={[
                            { value: "", label: jobs.length ? "Select a quoted job" : "No active jobs found" },
                            ...jobs.map((j) => ({
                              value: j.id,
                              label: `${j.title}${j.client_name ? ` — ${j.client_name}` : ""}`,
                            })),
                          ]}
                          testId="select-internal-job"
                        />
                      ) : (
                        <Input
                          label="Client Name *"
                          value={newUsage.client_name}
                          onChange={(e) => setNewUsage({ ...newUsage, client_name: e.target.value })}
                          required
                          placeholder="e.g., ABC Construction"
                          testId="input-client-name"
                        />
                      )}
                      <Input
                        label="Operator (selected below)"
                        value={
                          newUsage.operator_id
                            ? employees.find((u) => u.id === newUsage.operator_id)
                              ? `${employees.find((u) => u.id === newUsage.operator_id).first_name} ${employees.find((u) => u.id === newUsage.operator_id).last_name}`
                              : ""
                            : "—"
                        }
                        readOnly
                      />
                    </div>
                    <div className="grid md:grid-cols-3 gap-4">
                      <Input
                        label="Location/Site"
                        value={newUsage.location}
                        onChange={(e) => setNewUsage({...newUsage, location: e.target.value})}
                        placeholder="e.g., Melbourne CBD"
                      />
                      <div className="flex flex-col gap-1.5">
                        <label className="text-sm font-bold uppercase text-slate-600">Start Date</label>
                        <input
                          type="date"
                          value={newUsage.start_date}
                          onChange={(e) => setNewUsage({...newUsage, start_date: e.target.value})}
                          className="h-12 bg-white border-2 border-slate-200 focus:border-orange-500 focus:outline-none rounded-md px-3"
                        />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label className="text-sm font-bold uppercase text-slate-600">Expected End</label>
                        <input
                          type="date"
                          value={newUsage.expected_end_date}
                          onChange={(e) => setNewUsage({...newUsage, expected_end_date: e.target.value})}
                          className="h-12 bg-white border-2 border-slate-200 focus:border-orange-500 focus:outline-none rounded-md px-3"
                        />
                      </div>
                    </div>
                    <Select
                      label="Rate Type"
                      value={newUsage.rate_type}
                      onChange={(e) => setNewUsage({...newUsage, rate_type: e.target.value})}
                      options={[
                        { value: "hourly", label: "Hourly Rate" },
                        { value: "daily", label: "Daily Rate" },
                        { value: "weekly", label: "Weekly Rate" }
                      ]}
                    />
                    
                    {/* Operator and Dogman Selection */}
                    <div className="border-t pt-4 mt-4">
                      <h5 className="text-sm font-bold uppercase text-slate-600 mb-3">Personnel Assignment</h5>
                      <div className="grid md:grid-cols-2 gap-4">
                        <Select
                          label="Operator *"
                          value={newUsage.operator_id}
                          onChange={(e) => setNewUsage({...newUsage, operator_id: e.target.value})}
                          options={[
                            { value: "", label: "Select Operator" },
                            ...employees.filter(e => (e.capabilities || []).includes('operator'))
                              .map(e => ({ value: e.id, label: `${e.first_name} ${e.last_name} (${e.role})` }))
                          ]}
                          required
                        />
                        <div className="flex flex-col gap-1.5">
                          <label className="text-sm font-bold uppercase text-slate-600">Dogman(s)</label>
                          <div className="space-y-2">
                            <label className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={newUsage.no_dogman_required}
                                onChange={(e) => setNewUsage({
                                  ...newUsage, 
                                  no_dogman_required: e.target.checked,
                                  dogman_ids: e.target.checked ? [] : newUsage.dogman_ids
                                })}
                                className="w-4 h-4 rounded border-2 border-slate-300"
                              />
                              <span className="text-sm text-slate-600">No dogman required</span>
                            </label>
                            {!newUsage.no_dogman_required && (
                              <select
                                multiple
                                className="w-full h-24 bg-white border-2 border-slate-200 focus:border-orange-500 focus:outline-none rounded-md p-2 text-sm"
                                value={newUsage.dogman_ids}
                                onChange={(e) => {
                                  const selected = Array.from(e.target.selectedOptions, option => option.value);
                                  setNewUsage({...newUsage, dogman_ids: selected});
                                }}
                              >
                                {employees.filter(e => (e.capabilities || []).some(c => c === 'dogman' || c === 'rigger'))
                                  .map(e => (
                                    <option key={e.id} value={e.id}>
                                      {e.first_name} {e.last_name} ({e.role})
                                    </option>
                                  ))}
                              </select>
                            )}
                            {!newUsage.no_dogman_required && (
                              <p className="text-xs text-slate-500">Hold Ctrl/Cmd to select multiple</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Odometer Readings at Start */}
                    <div className="border-t pt-4 mt-4">
                      <h5 className="text-sm font-bold uppercase text-slate-600 mb-3">Odometer Readings at Start (Optional)</h5>
                      <div className="grid md:grid-cols-2 gap-4">
                        <Input
                          label="Starting Hours"
                          type="number"
                          step="0.1"
                          value={newUsage.starting_hours}
                          onChange={(e) => setNewUsage({...newUsage, starting_hours: e.target.value})}
                          placeholder="e.g., 1250.5"
                        />
                        <Input
                          label="Starting KMs"
                          type="number"
                          step="0.1"
                          value={newUsage.starting_kms}
                          onChange={(e) => setNewUsage({...newUsage, starting_kms: e.target.value})}
                          placeholder="e.g., 85420"
                        />
                      </div>
                    </div>
                    <Textarea
                      label="Notes"
                      value={newUsage.notes}
                      onChange={(e) => setNewUsage({...newUsage, notes: e.target.value})}
                      placeholder="Any relevant notes..."
                    />
                    <div className="flex gap-3">
                      <Button type="submit">
                        <Play className="w-4 h-4 mr-2" />
                        Start Tracking
                      </Button>
                      <Button type="button" variant="ghost" onClick={() => setShowNewUsage(false)}>
                        Cancel
                      </Button>
                    </div>
                  </form>
                </Card>
              )}
            </>
          )}

          {/* Usage History */}
          {usageHistory.length > 0 && (
            <div>
              <h4 className="font-bold uppercase mb-4">Usage History</h4>
              <div className="space-y-3">
                {usageHistory.filter(u => u.status !== 'active').map(usage => (
                  editingUsageId === usage.id ? (
                    <MachineUsageEditor
                      key={usage.id}
                      usage={usage}
                      machineId={machine.id}
                      onSaved={async () => {
                        setEditingUsageId(null);
                        await fetchMachineData();
                      }}
                      onCancel={() => setEditingUsageId(null)}
                    />
                  ) : (
                  <Card key={usage.id} className="bg-slate-50">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant={usage.status === 'completed' ? 'success' : 'warning'}>
                            {usage.status}
                          </Badge>
                          <span className="font-bold">{usage.client_name}</span>
                        </div>
                        <p className="text-sm text-slate-600">
                          {usage.location && <span>{usage.location} • </span>}
                          {new Date(usage.start_date).toLocaleDateString()} - {usage.actual_end_date ? new Date(usage.actual_end_date).toLocaleDateString() : 'Ongoing'}
                        </p>
                        {usage.job_title && (
                          <p className="text-xs text-blue-600">Job: {usage.job_title}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-orange-600">{usage.total_hours || 0}h</p>
                        <p className="text-xs text-slate-500">{usage.total_days || 0} days</p>
                        {canManageUsage && (
                          <button
                            onClick={() => setEditingUsageId(usage.id)}
                            className="mt-2 text-[11px] font-bold uppercase text-blue-600 hover:underline"
                            data-testid={`edit-usage-${usage.id}`}
                          >
                            Edit
                          </button>
                        )}
                      </div>
                    </div>
                  </Card>
                  )
                ))}
              </div>
            </div>
          )}

          {/* Complete Usage Modal */}
          {showCompleteUsage && activeUsage && (
            <Modal isOpen={showCompleteUsage} onClose={() => setShowCompleteUsage(false)} title="Complete Machine Usage">
              <form onSubmit={handleSubmitCompleteUsage} className="space-y-4">
                {/* End Date */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-bold uppercase text-slate-600">End Date</label>
                  <input
                    type="date"
                    value={completeUsageData.actual_end_date}
                    onChange={(e) => setCompleteUsageData({...completeUsageData, actual_end_date: e.target.value})}
                    className="h-12 bg-white border-2 border-slate-200 focus:border-orange-500 focus:outline-none rounded-md px-3"
                    required
                  />
                </div>

                {/* Odometer Readings at End */}
                <div className="border-t pt-4">
                  <h5 className="text-sm font-bold uppercase text-slate-600 mb-3">Odometer Readings at End</h5>
                  <div className="grid md:grid-cols-2 gap-4">
                    <Input
                      label="Ending Hours"
                      type="number"
                      step="0.1"
                      value={completeUsageData.ending_hours}
                      onChange={(e) => setCompleteUsageData({...completeUsageData, ending_hours: e.target.value})}
                      placeholder={activeUsage.starting_hours ? `Started at: ${activeUsage.starting_hours}` : "e.g., 1280.5"}
                    />
                    <Input
                      label="Ending KMs"
                      type="number"
                      step="0.1"
                      value={completeUsageData.ending_kms}
                      onChange={(e) => setCompleteUsageData({...completeUsageData, ending_kms: e.target.value})}
                      placeholder={activeUsage.starting_kms ? `Started at: ${activeUsage.starting_kms}` : "e.g., 85650"}
                    />
                  </div>
                  {activeUsage.starting_hours && (
                    <p className="text-xs text-slate-500 mt-1">
                      Usage: {completeUsageData.ending_hours ? (parseFloat(completeUsageData.ending_hours) - activeUsage.starting_hours).toFixed(1) : 0} hours
                    </p>
                  )}
                </div>

                {/* Machine Location */}
                <div className="border-t pt-4">
                  <h5 className="text-sm font-bold uppercase text-slate-600 mb-3">Machine Location After Use</h5>
                  <Select
                    label="Where is the machine?"
                    value={completeUsageData.machine_location}
                    onChange={(e) => setCompleteUsageData({...completeUsageData, machine_location: e.target.value})}
                    options={[
                      { value: "left_at_site", label: "Left at Site" },
                      { value: "back_to_yard", label: "Taken Back to Yard" },
                      { value: "other", label: "Other Location" }
                    ]}
                  />
                  {completeUsageData.machine_location === "other" && (
                    <Input
                      label="Location Address *"
                      value={completeUsageData.other_location_address}
                      onChange={(e) => setCompleteUsageData({...completeUsageData, other_location_address: e.target.value})}
                      placeholder="Enter the full address"
                      className="mt-3"
                      required
                    />
                  )}
                </div>

                {/* Receipt/Signature for Hired Machines */}
                {(activeUsage.rate_type === "hourly" || activeUsage.rate_type === "daily") && (
                  <div className="border-t pt-4">
                    <h5 className="text-sm font-bold uppercase text-slate-600 mb-3">Day Receipt / Client Signature</h5>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          Upload Signed Receipt Photo
                        </label>
                        {completeUsageData.receipt_photo_url ? (
                          <div className="flex items-center gap-3">
                            <CheckCircle className="w-5 h-5 text-green-500" />
                            <span className="text-sm text-green-700">Receipt photo uploaded</span>
                            <Button 
                              type="button"
                              variant="ghost" 
                              size="sm" 
                              onClick={() => setCompleteUsageData({...completeUsageData, receipt_photo_url: null})}
                            >
                              Remove
                            </Button>
                          </div>
                        ) : (
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleReceiptUpload(e.target.files[0])}
                            className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100"
                          />
                        )}
                      </div>
                      <p className="text-xs text-slate-500">
                        Upload a photo of the signed day receipt, or have the client sign the receipt directly on site.
                      </p>
                    </div>
                  </div>
                )}

                {/* Damages/Faults */}
                <div className="border-t pt-4">
                  <h5 className="text-sm font-bold uppercase text-slate-600 mb-3">Damages or Faults</h5>
                  <Textarea
                    label="Report any issues with the machine"
                    value={completeUsageData.damages_or_faults}
                    onChange={(e) => setCompleteUsageData({...completeUsageData, damages_or_faults: e.target.value})}
                    placeholder="Describe any damages, faults, or issues noticed during use. Leave blank if no issues."
                  />
                  {completeUsageData.damages_or_faults && (
                    <p className="text-xs text-amber-600 flex items-center gap-1 mt-2">
                      <AlertCircle className="w-4 h-4" />
                      Admins and managers will be notified of this issue
                    </p>
                  )}
                </div>

                {/* Notes */}
                <Textarea
                  label="Additional Notes"
                  value={completeUsageData.notes}
                  onChange={(e) => setCompleteUsageData({...completeUsageData, notes: e.target.value})}
                  placeholder="Any other notes about this usage period..."
                />

                {/* Submit Buttons */}
                <div className="flex gap-3 pt-4 border-t">
                  <Button type="submit">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Complete Usage
                  </Button>
                  <Button type="button" variant="ghost" onClick={() => setShowCompleteUsage(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            </Modal>
          )}
        </div>
      )}

      {/* Schedule Tab - Upcoming scheduled general hire jobs for this machine */}
      {activeTab === 'schedule' && machine && (
        <ScheduleTab machineId={machine.id} canEdit={canEdit} clients={clients} employees={employees} />
      )}

      {/* Hire Out Tab */}
      {activeTab === 'hire-out' && machine && (
        <div className="space-y-6">
          {/* Current Hire Status */}
          {machine.status === 'hired_out' && (
            <Card className="bg-amber-50 border-amber-200">
              <div className="flex items-center gap-3 mb-2">
                <Badge variant="warning">Currently Hired Out</Badge>
              </div>
              <p className="text-slate-700">
                This machine is currently hired out to <strong>{machine.current_hire_client || 'a client'}</strong>
              </p>
            </Card>
          )}

          {/* New Hire Out Form */}
          {canHireOut && machine.status !== 'hired_out' && (
            <>
              {!showNewHireOut ? (
                <div className="flex gap-2 flex-wrap">
                  <Button onClick={() => setShowNewHireOut(true)} data-testid="new-hire-out-machine-btn">
                    <Plus className="w-5 h-5 mr-2" /> Create New Hire Out
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => setShowDryHire(true)}
                    data-testid="start-dry-hire-btn"
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    <Plus className="w-5 h-5 mr-2" /> Start Dry Hire (with photos & sign-off)
                  </Button>
                </div>
              ) : (
                <Card className="bg-orange-50 border-orange-200">
                  <h4 className="font-bold uppercase mb-4">New Hire Out for {machine.name}</h4>
                  <form onSubmit={async (e) => {
                    e.preventDefault();
                    if (!newHireOut.client_id || !newHireOut.expected_return_date || !newHireOut.rate_amount || !newHireOut.hire_location) {
                      alert('Please fill in all required fields');
                      return;
                    }
                    try {
                      const payload = {
                        machine_id: machine.id,
                        client_id: newHireOut.client_id,
                        start_date: newHireOut.start_date,
                        start_time: newHireOut.start_time,
                        expected_return_date: newHireOut.expected_return_date,
                        rate_type: newHireOut.rate_type,
                        rate_amount: parseFloat(newHireOut.rate_amount),
                        delivery_method: newHireOut.delivery_method,
                        hire_location: newHireOut.hire_location,
                        hours_out: newHireOut.hours_out ? parseFloat(newHireOut.hours_out) : null,
                        km_out: newHireOut.km_out ? parseFloat(newHireOut.km_out) : null,
                        fuel_level_out: parseInt(newHireOut.fuel_level_out),
                        condition_notes_out: newHireOut.condition_notes_out,
                        client_provides_operator: newHireOut.client_provides_operator,
                        operator: newHireOut.client_provides_operator ? {
                          name: newHireOut.operator_name,
                          phone: newHireOut.operator_phone
                        } : null,
                        notes: newHireOut.notes
                      };
                      await axios.post(`${API}/hire-outs`, payload);
                      alert('Hire out created! Go to Hire Out page to complete checkout.');
                      setShowNewHireOut(false);
                      setNewHireOut({
                        client_id: "", start_date: new Date().toISOString().split('T')[0], start_time: "07:00",
                        expected_return_date: "", rate_type: "daily", rate_amount: "", delivery_method: "client_pickup",
                        hire_location: "", hours_out: machine.current_hours || "", km_out: machine.current_km || "",
                        fuel_level_out: 100, condition_notes_out: "", client_provides_operator: false,
                        operator_name: "", operator_phone: "", notes: ""
                      });
                      fetchMachineData();
                      onSave();
                    } catch (err) {
                      alert(err.response?.data?.detail || 'Failed to create hire out');
                    }
                  }} className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <Select
                        label="Client *"
                        value={newHireOut.client_id}
                        onChange={(e) => setNewHireOut({...newHireOut, client_id: e.target.value})}
                        options={[
                          { value: "", label: "-- Select Client --" },
                          ...clients.map(c => ({ value: c.id, label: c.name }))
                        ]}
                      />
                      <Input
                        label="Hire Location *"
                        value={newHireOut.hire_location}
                        onChange={(e) => setNewHireOut({...newHireOut, hire_location: e.target.value})}
                        placeholder="Site address"
                      />
                    </div>
                    <div className="grid md:grid-cols-4 gap-4">
                      <Input label="Start Date *" type="date" value={newHireOut.start_date}
                        onChange={(e) => setNewHireOut({...newHireOut, start_date: e.target.value})} />
                      <Input label="Start Time" type="time" value={newHireOut.start_time}
                        onChange={(e) => setNewHireOut({...newHireOut, start_time: e.target.value})} />
                      <Input label="Expected Return *" type="date" value={newHireOut.expected_return_date}
                        onChange={(e) => setNewHireOut({...newHireOut, expected_return_date: e.target.value})} />
                      <Select label="Delivery" value={newHireOut.delivery_method}
                        onChange={(e) => setNewHireOut({...newHireOut, delivery_method: e.target.value})}
                        options={[{ value: "client_pickup", label: "Client Pickup" }, { value: "company_delivers", label: "We Deliver" }]} />
                    </div>
                    <div className="grid md:grid-cols-3 gap-4">
                      <Select label="Rate Type" value={newHireOut.rate_type}
                        onChange={(e) => setNewHireOut({...newHireOut, rate_type: e.target.value})}
                        options={[{ value: "hourly", label: "Hourly" }, { value: "daily", label: "Daily" }, { value: "weekly", label: "Weekly" }]} />
                      <Input label="Rate Amount ($) *" type="number" value={newHireOut.rate_amount}
                        onChange={(e) => setNewHireOut({...newHireOut, rate_amount: e.target.value})} placeholder="e.g., 850" />
                      <Input label="Fuel Level (%)" type="number" min="0" max="100" value={newHireOut.fuel_level_out}
                        onChange={(e) => setNewHireOut({...newHireOut, fuel_level_out: e.target.value})} />
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <Input label="Hours Reading" type="number" value={newHireOut.hours_out}
                        onChange={(e) => setNewHireOut({...newHireOut, hours_out: e.target.value})} />
                      <Input label="KM Reading" type="number" value={newHireOut.km_out}
                        onChange={(e) => setNewHireOut({...newHireOut, km_out: e.target.value})} />
                    </div>
                    <Textarea label="Condition Notes" value={newHireOut.condition_notes_out}
                      onChange={(e) => setNewHireOut({...newHireOut, condition_notes_out: e.target.value})}
                      placeholder="Current condition of the machine..." />
                    <div className="flex items-center gap-3">
                      <input type="checkbox" id="clientOp" checked={newHireOut.client_provides_operator}
                        onChange={(e) => setNewHireOut({...newHireOut, client_provides_operator: e.target.checked})} className="w-5 h-5" />
                      <label htmlFor="clientOp" className="font-medium">Client provides operator</label>
                    </div>
                    {newHireOut.client_provides_operator && (
                      <div className="grid md:grid-cols-2 gap-4">
                        <Input label="Operator Name" value={newHireOut.operator_name}
                          onChange={(e) => setNewHireOut({...newHireOut, operator_name: e.target.value})} />
                        <Input label="Operator Phone" value={newHireOut.operator_phone}
                          onChange={(e) => setNewHireOut({...newHireOut, operator_phone: e.target.value})} />
                      </div>
                    )}
                    <Textarea label="Notes" value={newHireOut.notes}
                      onChange={(e) => setNewHireOut({...newHireOut, notes: e.target.value})} placeholder="Any notes..." />
                    <div className="flex gap-3">
                      <Button type="submit">Create Hire Out</Button>
                      <Button type="button" variant="ghost" onClick={() => setShowNewHireOut(false)}>Cancel</Button>
                    </div>
                  </form>
                </Card>
              )}
            </>
          )}

          {/* Hire Out History */}
          <div>
            <h4 className="font-bold uppercase mb-3">Hire Out History ({hireOutHistory.length})</h4>
            {hireOutHistory.length === 0 ? (
              <Card className="text-center py-8 text-slate-500">
                <Truck className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                <p>No hire out history for this machine</p>
              </Card>
            ) : (
              <div className="space-y-3">
                {hireOutHistory.map(hire => (
                  <Card key={hire.id} className={`${hire.status === 'out_on_hire' || hire.status === 'overdue' ? 'bg-amber-50 border-amber-200' : ''}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant={
                            hire.status === 'completed' ? 'success' :
                            hire.status === 'overdue' ? 'destructive' :
                            hire.status === 'out_on_hire' ? 'warning' : 'default'
                          }>
                            {hire.status === 'out_on_hire' ? 'Out on Hire' : hire.status?.replace('_', ' ')}
                          </Badge>
                        </div>
                        <p className="font-bold">{hire.client_name}</p>
                        <p className="text-sm text-slate-600">{hire.hire_location}</p>
                        <p className="text-sm text-slate-500">{hire.start_date} → {hire.actual_return_date || hire.expected_return_date}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-orange-600">${hire.rate_amount}/{hire.rate_type}</p>
                        {hire.total_days && <p className="text-sm text-slate-600">{hire.total_days} days</p>}
                        {hire.total_charges > 0 && <p className="text-sm text-red-600">+${hire.total_charges} charges</p>}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Documents Tab */}
      {activeTab === 'documents' && machine && (
        <div className="space-y-6">
          {/* Add Document Form */}
          {canEdit && (
            <Card className="bg-slate-50">
              <h4 className="font-bold uppercase mb-4">Add Document</h4>
              <form onSubmit={handleAddDocument} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <Input
                    label="Document Name"
                    value={newDoc.name}
                    onChange={(e) => setNewDoc({...newDoc, name: e.target.value})}
                    required
                  />
                  <Select
                    label="Type"
                    value={newDoc.document_type}
                    onChange={(e) => setNewDoc({...newDoc, document_type: e.target.value})}
                    options={Object.entries(docTypeLabels).map(([v, l]) => ({ value: v, label: l }))}
                  />
                  <Input
                    label="Expiry Date (optional)"
                    type="date"
                    value={newDoc.expiry_date}
                    onChange={(e) => setNewDoc({...newDoc, expiry_date: e.target.value})}
                  />
                  <Input
                    label="Description (optional)"
                    value={newDoc.description}
                    onChange={(e) => setNewDoc({...newDoc, description: e.target.value})}
                  />
                </div>
                <FileUploadInput
                  label="Document File (PDF / Image) *"
                  accept="application/pdf,image/*"
                  onFileSelect={(url, name) => setNewDoc({...newDoc, file_url: url, name: newDoc.name || name})}
                  currentFile={newDoc.file_url ? (newDoc.name || "Document attached") : null}
                  onClear={() => setNewDoc({...newDoc, file_url: ""})}
                />
                <div className="flex justify-end">
                  <Button type="submit" disabled={!newDoc.file_url} data-testid="machine-doc-submit">
                    <Plus className="w-4 h-4 mr-1" /> Upload Document
                  </Button>
                </div>
              </form>
            </Card>
          )}

          {/* Documents List */}
          <div className="space-y-3" data-testid="machine-documents-list">
            {documents.map(doc => (
              <Card key={doc.id} className="flex items-center justify-between flex-wrap gap-3" data-testid={`machine-doc-${doc.id}`}>
                <div className="flex items-center gap-4 min-w-0">
                  <Shield className="w-8 h-8 text-blue-600 flex-shrink-0" />
                  <div className="min-w-0">
                    <h4 className="font-bold truncate">{doc.name}</h4>
                    <p className="text-sm text-slate-500">{docTypeLabels[doc.document_type] || doc.document_type}</p>
                    {doc.expiry_date && (
                      <p className="text-xs text-orange-600">Expires: {doc.expiry_date}</p>
                    )}
                    {doc.description && <p className="text-xs text-slate-500 italic">{doc.description}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {doc.file_url && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => window.open(doc.file_url, "_blank")} data-testid={`machine-doc-view-${doc.id}`}>
                        View
                      </Button>
                      <Button size="sm" variant="outline" onClick={async () => {
                        try {
                          const r = await fetch(doc.file_url);
                          const blob = await r.blob();
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement("a");
                          a.href = url; a.download = doc.name || "document"; a.click();
                          URL.revokeObjectURL(url);
                        } catch (err) { window.open(doc.file_url, "_blank"); }
                      }} data-testid={`machine-doc-dl-${doc.id}`}>
                        <Download className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                  {canEdit && (
                    <Button size="sm" variant="ghost" onClick={() => handleDeleteDocument(doc.id)} data-testid={`machine-doc-del-${doc.id}`}>
                      <Trash2 className="w-4 h-4 text-rose-500" />
                    </Button>
                  )}
                </div>
              </Card>
            ))}
            {documents.length === 0 && (
              <p className="text-center text-slate-500 py-8">No documents uploaded yet. Upload safety certs, rego, manuals, insurance — workers on site can view + download anything you add here.</p>
            )}
          </div>
        </div>
      )}

      {/* Photos Tab */}
      {activeTab === 'photos' && machine && (
        <div className="space-y-6">
          {/* Add Photo Form */}
          <Card className="bg-slate-50">
            <h4 className="font-bold uppercase mb-4">Add Photo</h4>
            <form onSubmit={handleAddPhoto} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <Input
                  label="Title"
                  value={newPhoto.title}
                  onChange={(e) => setNewPhoto({...newPhoto, title: e.target.value})}
                  required
                />
                <Select
                  label="Photo Type"
                  value={newPhoto.photo_type}
                  onChange={(e) => setNewPhoto({...newPhoto, photo_type: e.target.value})}
                  options={Object.entries(photoTypeLabels).map(([v, l]) => ({ value: v, label: l }))}
                />
              </div>
              <FileUploadInput
                label="Upload Photo"
                accept=".jpg,.jpeg,.png,.gif,.webp"
                onFileSelect={(fileUrl, fileName) => setNewPhoto({...newPhoto, file_url: fileUrl})}
                currentFile={newPhoto.file_url ? "Photo uploaded" : null}
                onClear={() => setNewPhoto({...newPhoto, file_url: ""})}
              />
              <Textarea
                label="Description"
                value={newPhoto.description}
                onChange={(e) => setNewPhoto({...newPhoto, description: e.target.value})}
              />
              <Button type="submit" size="sm" disabled={!newPhoto.file_url}>
                <Camera className="w-4 h-4 mr-1" /> Upload Photo
              </Button>
            </form>
          </Card>

          {/* Photos Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {photos.map(photo => (
              <div key={photo.id} className="relative group">
                <img 
                  src={photo.file_url} 
                  alt={photo.title}
                  className="w-full h-40 object-cover rounded-lg border-2 border-slate-200"
                  onError={(e) => { e.target.src = 'https://via.placeholder.com/300x200?text=No+Image'; }}
                />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex flex-col items-center justify-center text-white p-2">
                  <p className="font-bold text-sm text-center">{photo.title}</p>
                  <Badge variant={photo.photo_type === 'damage' ? 'error' : 'default'} className="mt-2 text-xs">
                    {photoTypeLabels[photo.photo_type]}
                  </Badge>
                  <button
                    onClick={() => handleDeletePhoto(photo.id)}
                    className="absolute top-2 right-2 p-1 bg-red-600 rounded hover:bg-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
            {photos.length === 0 && (
              <div className="col-span-full text-center text-slate-500 py-8">
                <Camera className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>No photos uploaded yet</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Jobs Tab */}
      {activeTab === 'jobs' && machine && canEdit && (
        <div className="space-y-6">
          <h4 className="font-bold uppercase">Assign to Jobs</h4>
          <div className="max-h-64 overflow-y-auto space-y-2">
            {jobs.map(job => (
              <label 
                key={job.id}
                className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                  assignedJobs.includes(job.id) ? 'border-orange-500 bg-orange-50' : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <input
                  type="checkbox"
                  checked={assignedJobs.includes(job.id)}
                  onChange={() => {
                    setAssignedJobs(prev => 
                      prev.includes(job.id) ? prev.filter(id => id !== job.id) : [...prev, job.id]
                    );
                  }}
                  className="w-5 h-5"
                />
                <div>
                  <p className="font-semibold">{job.title}</p>
                  <p className="text-sm text-slate-500">{job.location}</p>
                </div>
              </label>
            ))}
          </div>
          <Button onClick={handleAssignJobs}>Save Job Assignments</Button>
        </div>
      )}
      {machineConflict && (
        <MachineConflictModal
          conflict={machineConflict}
          onClose={() => setMachineConflict(null)}
          onResolved={() => {
            setMachineConflict(null);
            // Retry the original action automatically
            if (showNewUsage) handleStartUsage({ preventDefault: () => {} });
            fetchMachineData();
          }}
        />
      )}
      {readingMismatch && (
        <ReadingMismatchModal
          mismatch={readingMismatch}
          onClose={() => setReadingMismatch(null)}
          onConfirm={async (reason) => {
            try {
              const usageData = {
                ...newUsage,
                starting_hours: newUsage.starting_hours ? parseFloat(newUsage.starting_hours) : null,
                starting_kms: newUsage.starting_kms ? parseFloat(newUsage.starting_kms) : null,
                reconciliation_reason: reason,
              };
              await axios.post(`${API}/machines/${machine.id}/usage`, usageData);
              alert("Machine usage tracking started — discrepancy logged.");
              setReadingMismatch(null);
              setShowNewUsage(false);
              fetchMachineData();
              onSave();
            } catch (e) {
              alert(e.response?.data?.detail || "Failed to start usage tracking");
            }
          }}
        />
      )}

      {/* Registry Tab — Maintenance / Lift / Equipment */}
      {activeTab === 'registry' && machine && (
        <MachineRegistryTab machineId={machine.id} canEdit={canEdit} />
      )}

      {/* Receipts/Travel/Timeline content all consolidated into Activity tab below */}
      {activeTab === 'activity' && machine && (
        <div className="mt-6 space-y-4">
          <MachineTimelineTab machineId={machine.id} currentUser={user} />
        </div>
      )}

      {/* Dry Hire start modal — admin/manager/office/supervisor only */}
      {showDryHire && machine && (
        <Modal isOpen={showDryHire} onClose={() => setShowDryHire(false)} size="xl">
          <DryHireStartModal
            machine={machine}
            clients={clients || []}
            currentUser={user}
            onClose={() => setShowDryHire(false)}
            onCreated={() => fetchMachineData()}
          />
        </Modal>
      )}
    </Modal>
  );
};

export default MachinesPage;
